import { useState, useEffect, useRef, useCallback } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import {
  Brain,
  Upload,
  Globe,
  FileText,
  Trash2,
  RefreshCw,
  Plus,
  Check,
  AlertCircle,
  X,
  Zap,
  Image,
  Video,
  MessageSquare,
  Search,
  ChevronRight,
  Sparkles,
  FileImage,
  FileVideo,
  File,
  ExternalLink,
  Lightbulb,
  ArrowRight,
  Send,
  Eye,
  Bold,
  Italic,
  Strikethrough,
  Heading1,
  Heading2,
  Heading3,
  List,
  ListOrdered,
  Quote,
  Minus,
  Lock,
  Unlock,
} from 'lucide-react';
import { useAuthStore } from '../store/auth';
import { useThemeStore } from '../store/theme';
import { notify } from '../store/notifications';
import { useWorkspaceStore } from '../store/workspace';

const API_URL = import.meta.env.VITE_API_URL || '/api';

const fadeInUp = {
  hidden: { opacity: 0, y: 20 },
  visible: { opacity: 1, y: 0 },
};

type ContentFilter = 'all' | 'memories' | 'images' | 'videos' | 'webpages' | 'documents';

interface BrainDocument {
  id: number;
  name: string;
  type: string;
  source_url?: string;
  status: 'processing' | 'ready' | 'error';
  chunks_count: number;
  content_summary?: string;
  error_message?: string;
  created_at: string;
  updated_at: string;
}

interface BrainStats {
  total_documents: number;
  total_chunks: number;
  ready_count: number;
  processing_count: number;
  error_count: number;
  last_updated: string | null;
}

interface KnowledgeEntry {
  id: number;
  key: string;
  label: string;
  value: string;
  category: string;
  source: string;
  created_at: string;
  updated_at: string;
}

interface Provider {
  id: string;
  name: string;
  description: string;
  category: string;
  icon: string;
  color: string;
  features: string[];
  authType: 'oauth2' | 'api_key' | 'webhook';
  isAvailable: boolean;
}

interface Integration {
  id: number;
  provider: string;
  provider_name: string;
  provider_username?: string;
  provider_email?: string;
  status: 'connected' | 'disconnected' | 'error';
}

// Provider Icon component with actual brand logos
const ProviderIcon = ({ providerId, providerName, color, connected }: { providerId: string; providerName: string; color: string; connected?: boolean }) => {
  const iconClass = "w-6 h-6";

  // Map provider IDs to their SVG icons
  const getIcon = () => {
    const id = providerId.toLowerCase();

    if (id.includes('facebook')) {
      return (
        <svg viewBox="0 0 24 24" className={iconClass}>
          <path fill="#1877F2" d="M24 12.073c0-6.627-5.373-12-12-12s-12 5.373-12 12c0 5.99 4.388 10.954 10.125 11.854v-8.385H7.078v-3.47h3.047V9.43c0-3.007 1.792-4.669 4.533-4.669 1.312 0 2.686.235 2.686.235v2.953H15.83c-1.491 0-1.956.925-1.956 1.874v2.25h3.328l-.532 3.47h-2.796v8.385C19.612 23.027 24 18.062 24 12.073z"/>
        </svg>
      );
    }

    if (id.includes('instagram')) {
      return (
        <svg viewBox="0 0 24 24" className={iconClass}>
          <defs>
            <linearGradient id="instagram-gradient" x1="0%" y1="100%" x2="100%" y2="0%">
              <stop offset="0%" stopColor="#FFDC80"/>
              <stop offset="25%" stopColor="#FCAF45"/>
              <stop offset="50%" stopColor="#F77737"/>
              <stop offset="75%" stopColor="#F56040"/>
              <stop offset="100%" stopColor="#FD1D1D"/>
            </linearGradient>
            <linearGradient id="instagram-gradient2" x1="0%" y1="100%" x2="100%" y2="0%">
              <stop offset="0%" stopColor="#833AB4"/>
              <stop offset="100%" stopColor="#C13584"/>
            </linearGradient>
          </defs>
          <path fill="url(#instagram-gradient)" d="M12 2.163c3.204 0 3.584.012 4.85.07 3.252.148 4.771 1.691 4.919 4.919.058 1.265.069 1.645.069 4.849 0 3.205-.012 3.584-.069 4.849-.149 3.225-1.664 4.771-4.919 4.919-1.266.058-1.644.07-4.85.07-3.204 0-3.584-.012-4.849-.07-3.26-.149-4.771-1.699-4.919-4.92-.058-1.265-.07-1.644-.07-4.849 0-3.204.013-3.583.07-4.849.149-3.227 1.664-4.771 4.919-4.919 1.266-.057 1.645-.069 4.849-.069zM12 0C8.741 0 8.333.014 7.053.072 2.695.272.273 2.69.073 7.052.014 8.333 0 8.741 0 12c0 3.259.014 3.668.072 4.948.2 4.358 2.618 6.78 6.98 6.98C8.333 23.986 8.741 24 12 24c3.259 0 3.668-.014 4.948-.072 4.354-.2 6.782-2.618 6.979-6.98.059-1.28.073-1.689.073-4.948 0-3.259-.014-3.667-.072-4.947-.196-4.354-2.617-6.78-6.979-6.98C15.668.014 15.259 0 12 0zm0 5.838a6.162 6.162 0 100 12.324 6.162 6.162 0 000-12.324zM12 16a4 4 0 110-8 4 4 0 010 8zm6.406-11.845a1.44 1.44 0 100 2.881 1.44 1.44 0 000-2.881z"/>
        </svg>
      );
    }

    if (id.includes('twitter') || id.includes('x')) {
      return (
        <svg viewBox="0 0 24 24" className={iconClass}>
          <path fill="#000" d="M18.244 2.25h3.308l-7.227 8.26 8.502 11.24H16.17l-5.214-6.817L4.99 21.75H1.68l7.73-8.835L1.254 2.25H8.08l4.713 6.231zm-1.161 17.52h1.833L7.084 4.126H5.117z"/>
        </svg>
      );
    }

    if (id.includes('gmail') || id.includes('google')) {
      return (
        <svg viewBox="0 0 24 24" className={iconClass}>
          <path fill="#4285F4" d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"/>
          <path fill="#34A853" d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"/>
          <path fill="#FBBC05" d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z"/>
          <path fill="#EA4335" d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"/>
        </svg>
      );
    }

    if (id.includes('linkedin')) {
      return (
        <svg viewBox="0 0 24 24" className={iconClass}>
          <path fill="#0A66C2" d="M20.447 20.452h-3.554v-5.569c0-1.328-.027-3.037-1.852-3.037-1.853 0-2.136 1.445-2.136 2.939v5.667H9.351V9h3.414v1.561h.046c.477-.9 1.637-1.85 3.37-1.85 3.601 0 4.267 2.37 4.267 5.455v6.286zM5.337 7.433c-1.144 0-2.063-.926-2.063-2.065 0-1.138.92-2.063 2.063-2.063 1.14 0 2.064.925 2.064 2.063 0 1.139-.925 2.065-2.064 2.065zm1.782 13.019H3.555V9h3.564v11.452zM22.225 0H1.771C.792 0 0 .774 0 1.729v20.542C0 23.227.792 24 1.771 24h20.451C23.2 24 24 23.227 24 22.271V1.729C24 .774 23.2 0 22.222 0h.003z"/>
        </svg>
      );
    }

    if (id.includes('slack')) {
      return (
        <svg viewBox="0 0 24 24" className={iconClass}>
          <path fill="#E01E5A" d="M5.042 15.165a2.528 2.528 0 0 1-2.52 2.523A2.528 2.528 0 0 1 0 15.165a2.527 2.527 0 0 1 2.522-2.52h2.52v2.52zM6.313 15.165a2.527 2.527 0 0 1 2.521-2.52 2.527 2.527 0 0 1 2.521 2.52v6.313A2.528 2.528 0 0 1 8.834 24a2.528 2.528 0 0 1-2.521-2.522v-6.313z"/>
          <path fill="#36C5F0" d="M8.834 5.042a2.528 2.528 0 0 1-2.521-2.52A2.528 2.528 0 0 1 8.834 0a2.528 2.528 0 0 1 2.521 2.522v2.52H8.834zM8.834 6.313a2.528 2.528 0 0 1 2.521 2.521 2.528 2.528 0 0 1-2.521 2.521H2.522A2.528 2.528 0 0 1 0 8.834a2.528 2.528 0 0 1 2.522-2.521h6.312z"/>
          <path fill="#2EB67D" d="M18.956 8.834a2.528 2.528 0 0 1 2.522-2.521A2.528 2.528 0 0 1 24 8.834a2.528 2.528 0 0 1-2.522 2.521h-2.522V8.834zM17.688 8.834a2.528 2.528 0 0 1-2.523 2.521 2.527 2.527 0 0 1-2.52-2.521V2.522A2.527 2.527 0 0 1 15.165 0a2.528 2.528 0 0 1 2.523 2.522v6.312z"/>
          <path fill="#ECB22E" d="M15.165 18.956a2.528 2.528 0 0 1 2.523 2.522A2.528 2.528 0 0 1 15.165 24a2.527 2.527 0 0 1-2.52-2.522v-2.522h2.52zM15.165 17.688a2.527 2.527 0 0 1-2.52-2.523 2.526 2.526 0 0 1 2.52-2.52h6.313A2.527 2.527 0 0 1 24 15.165a2.528 2.528 0 0 1-2.522 2.523h-6.313z"/>
        </svg>
      );
    }

    if (id.includes('trello')) {
      return (
        <svg viewBox="0 0 24 24" className={iconClass}>
          <path fill="#0052CC" d="M21 0H3C1.343 0 0 1.343 0 3v18c0 1.656 1.343 3 3 3h18c1.656 0 3-1.344 3-3V3c0-1.657-1.344-3-3-3zM10.44 18.18c0 .795-.645 1.44-1.44 1.44H4.56c-.795 0-1.44-.645-1.44-1.44V4.56c0-.795.645-1.44 1.44-1.44H9c.795 0 1.44.645 1.44 1.44v13.62zm10.44-6c0 .794-.645 1.44-1.44 1.44H15c-.795 0-1.44-.646-1.44-1.44V4.56c0-.795.645-1.44 1.44-1.44h4.44c.795 0 1.44.645 1.44 1.44v7.62z"/>
        </svg>
      );
    }

    if (id.includes('hubspot')) {
      return (
        <svg viewBox="0 0 24 24" className={iconClass}>
          <path fill="#FF7A59" d="M18.164 7.93V5.084a2.198 2.198 0 001.267-1.984v-.066A2.198 2.198 0 0017.235.836h-.067a2.198 2.198 0 00-2.198 2.198v.066c0 .907.55 1.685 1.335 2.02v2.777a6.467 6.467 0 00-3.394 1.456l-7.4-5.56a2.218 2.218 0 00.063-.502 2.252 2.252 0 10-2.252 2.252c.397 0 .769-.104 1.092-.286l7.266 5.453a6.476 6.476 0 00-.415 2.287c0 .826.156 1.614.438 2.34L4.91 20.635a2.198 2.198 0 00-1.335-.455 2.252 2.252 0 102.252 2.252c0-.574-.217-1.097-.571-1.494l6.695-5.254a6.462 6.462 0 004.3 1.634 6.476 6.476 0 006.476-6.476 6.449 6.449 0 00-4.563-6.911zm-1.912 10.138a3.298 3.298 0 01-3.298-3.297 3.298 3.298 0 016.596 0 3.298 3.298 0 01-3.298 3.297z"/>
        </svg>
      );
    }

    if (id.includes('notion')) {
      return (
        <svg viewBox="0 0 24 24" className={iconClass}>
          <path fill="#000" d="M4.459 4.208c.746.606 1.026.56 2.428.466l13.215-.793c.28 0 .047-.28-.046-.326L17.86 2.04c-.42-.326-.98-.7-2.055-.607L3.01 2.54c-.466.046-.56.28-.374.466l1.823 1.202zm.793 3.08v13.904c0 .747.373 1.027 1.214.98l14.523-.84c.841-.046.935-.56.935-1.167V6.354c0-.606-.233-.933-.748-.886l-15.177.887c-.56.047-.747.327-.747.933zm14.337.745c.093.42 0 .84-.42.888l-.7.14v10.264c-.608.327-1.168.514-1.635.514-.748 0-.935-.234-1.495-.933l-4.577-7.186v6.952l1.449.327s0 .84-1.168.84l-3.222.186c-.093-.186 0-.653.327-.746l.84-.233V9.854L7.822 9.76c-.094-.42.14-1.026.793-1.073l3.456-.233 4.764 7.279v-6.44l-1.215-.14c-.093-.514.28-.886.747-.933l3.222-.186zM2.332.933L16.24 0c1.68-.14 2.1.093 2.8.606l3.876 2.754c.467.327.607.42.607.98v17.5c0 1.12-.42 1.773-1.869 1.866l-15.457.934c-1.12.046-1.68-.14-2.24-.84L.933 21.467c-.467-.607-.654-1.073-.654-1.773V2.613c0-.886.42-1.586 2.053-1.68z"/>
        </svg>
      );
    }

    if (id.includes('tiktok')) {
      return (
        <svg viewBox="0 0 24 24" className={iconClass}>
          <path fill="#000" d="M12.525.02c1.31-.02 2.61-.01 3.91-.02.08 1.53.63 3.09 1.75 4.17 1.12 1.11 2.7 1.62 4.24 1.79v4.03c-1.44-.05-2.89-.35-4.2-.97-.57-.26-1.1-.59-1.62-.93-.01 2.92.01 5.84-.02 8.75-.08 1.4-.54 2.79-1.35 3.94-1.31 1.92-3.58 3.17-5.91 3.21-1.43.08-2.86-.31-4.08-1.03-2.02-1.19-3.44-3.37-3.65-5.71-.02-.5-.03-1-.01-1.49.18-1.9 1.12-3.72 2.58-4.96 1.66-1.44 3.98-2.13 6.15-1.72.02 1.48-.04 2.96-.04 4.44-.99-.32-2.15-.23-3.02.37-.63.41-1.11 1.04-1.36 1.75-.21.51-.15 1.07-.14 1.61.24 1.64 1.82 3.02 3.5 2.87 1.12-.01 2.19-.66 2.77-1.61.19-.33.4-.67.41-1.06.1-1.79.06-3.57.07-5.36.01-4.03-.01-8.05.02-12.07z"/>
        </svg>
      );
    }

    if (id.includes('pinterest')) {
      return (
        <svg viewBox="0 0 24 24" className={iconClass}>
          <path fill="#E60023" d="M12.017 0C5.396 0 .029 5.367.029 11.987c0 5.079 3.158 9.417 7.618 11.162-.105-.949-.199-2.403.041-3.439.219-.937 1.406-5.957 1.406-5.957s-.359-.72-.359-1.781c0-1.663.967-2.911 2.168-2.911 1.024 0 1.518.769 1.518 1.688 0 1.029-.653 2.567-.992 3.992-.285 1.193.6 2.165 1.775 2.165 2.128 0 3.768-2.245 3.768-5.487 0-2.861-2.063-4.869-5.008-4.869-3.41 0-5.409 2.562-5.409 5.199 0 1.033.394 2.143.889 2.741.099.12.112.225.085.345-.09.375-.293 1.199-.334 1.363-.053.225-.172.271-.401.165-1.495-.69-2.433-2.878-2.433-4.646 0-3.776 2.748-7.252 7.92-7.252 4.158 0 7.392 2.967 7.392 6.923 0 4.135-2.607 7.462-6.233 7.462-1.214 0-2.354-.629-2.758-1.379l-.749 2.848c-.269 1.045-1.004 2.352-1.498 3.146 1.123.345 2.306.535 3.55.535 6.607 0 11.985-5.365 11.985-11.987C23.97 5.39 18.592.026 11.985.026L12.017 0z"/>
        </svg>
      );
    }

    if (id.includes('outlook')) {
      return (
        <svg viewBox="0 0 24 24" className={iconClass}>
          <path fill="#0078D4" d="M24 7.387v10.478c0 .23-.08.424-.238.576-.16.154-.352.232-.576.232h-8.234v-6.18l1.3 1.012c.083.07.187.106.312.106.125 0 .229-.035.312-.106l6.97-5.454c.067-.06.123-.104.168-.13a.272.272 0 0 1 .143-.04c.09 0 .164.035.22.104.057.07.086.15.086.24v.39c0 .094-.03.177-.09.252l-7.064 5.565a.55.55 0 0 1-.34.117.55.55 0 0 1-.34-.117l-1.627-1.27v6.11H.814c-.224 0-.416-.078-.576-.232A.774.774 0 0 1 0 17.864V7.387l.238-.576L.576 6.3H14.952v.51l.576.577h7.658c.224 0 .416.078.576.232.158.153.238.347.238.576v-.002l-.001-.806z"/>
          <path fill="#0078D4" d="M7.5 10.5c-1.933 0-3.5 1.567-3.5 3.5s1.567 3.5 3.5 3.5S11 15.933 11 14s-1.567-3.5-3.5-3.5zm0 5.5c-1.103 0-2-.897-2-2s.897-2 2-2 2 .897 2 2-.897 2-2 2z"/>
        </svg>
      );
    }

    if (id.includes('salesforce')) {
      return (
        <svg viewBox="0 0 24 24" className={iconClass}>
          <path fill="#00A1E0" d="M10.006 5.415a4.195 4.195 0 0 1 3.045-1.306c1.56 0 2.954.9 3.69 2.205.63-.3 1.35-.45 2.1-.45 2.85 0 5.159 2.34 5.159 5.22s-2.31 5.22-5.16 5.22c-.45 0-.9-.06-1.305-.165a3.9 3.9 0 0 1-3.63 2.49 3.885 3.885 0 0 1-2.73-1.11 4.71 4.71 0 0 1-3.93 2.13c-2.625 0-4.755-2.16-4.755-4.83 0-.57.105-1.11.285-1.62A3.69 3.69 0 0 1 0 10.2c0-2.07 1.665-3.75 3.72-3.75.51 0 1.005.105 1.455.3a4.2 4.2 0 0 1 4.83-1.335z"/>
        </svg>
      );
    }

    if (id.includes('pipedrive')) {
      return (
        <svg viewBox="0 0 24 24" className={iconClass}>
          <path fill="#017737" d="M12 0C5.373 0 0 5.373 0 12s5.373 12 12 12 12-5.373 12-12S18.627 0 12 0zm0 18.75a6.75 6.75 0 1 1 0-13.5 6.75 6.75 0 0 1 0 13.5z"/>
          <circle fill="#017737" cx="12" cy="12" r="3"/>
        </svg>
      );
    }

    if (id.includes('quickbooks')) {
      return (
        <svg viewBox="0 0 24 24" className={iconClass}>
          <path fill="#2CA01C" d="M12 0C5.373 0 0 5.373 0 12s5.373 12 12 12 12-5.373 12-12S18.627 0 12 0zm3.6 16.8H13.2v1.2h-2.4v-1.2H8.4V6h6c1.988 0 3.6 1.612 3.6 3.6v3.6c0 1.988-1.612 3.6-3.6 3.6zm0-7.2h-3.6v4.8h3.6c.662 0 1.2-.538 1.2-1.2v-2.4c0-.662-.538-1.2-1.2-1.2z"/>
        </svg>
      );
    }

    if (id.includes('strava')) {
      return (
        <svg viewBox="0 0 24 24" className={iconClass}>
          <path fill="#FC4C02" d="M15.387 17.944l-2.089-4.116h-3.065L15.387 24l5.15-10.172h-3.066m-7.008-5.599l2.836 5.598h4.172L10.463 0l-7 13.828h4.169"/>
        </svg>
      );
    }

    if (id.includes('whatsapp')) {
      return (
        <svg viewBox="0 0 24 24" className={iconClass}>
          <path fill="#25D366" d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413z"/>
        </svg>
      );
    }

    if (id.includes('shopify')) {
      return (
        <svg viewBox="0 0 24 24" className={iconClass}>
          <path fill="#96BF48" d="M15.337 23.979l7.216-1.561s-2.604-17.613-2.625-17.756c-.021-.143-.163-.227-.284-.248s-2.139-.163-2.139-.163-.566-.567-.78-.787a.726.726 0 00-.117-.063l-.89 19.579zm-1.098-19.249c-.084.021-.167.042-.271.063l-.041-1.047c-.064-.787-.55-1.171-1.177-1.193.248-.57.757-.867 1.379-.867 1.097.021 1.649.997 1.649 1.049l-.063 1.393c-.375.148-.766.317-1.177.465-.106.041-.192.084-.299.137zm-.632-.886l-.063-.866c0-.339-.126-.571-.339-.74.827.021 1.24.867 1.24.91l-.063.801c-.252.086-.504.171-.775.287.064-.148-.021-.392 0-.392zm-1.891 5.305c.063 1.005 2.69.823 2.835 3.597.106 2.181-1.157 3.681-3.022 3.787-2.242.148-3.48-1.179-3.48-1.179l.481-2.034s1.241.927 2.222.866c.644-.042.878-.571.857-.93-.085-1.325-2.223-1.244-2.352-3.406-.106-1.812 1.073-3.659 3.703-3.829 1.012-.063 1.533.192 1.533.192l-.566 2.138s-.673-.317-1.469-.254c-1.157.084-1.177.803-1.157 1.014-.001.004.414.038.415.038zM12.19.948c.084.021.147.063.209.084l-.211 4.595c-.19.079-.401.147-.612.225l-.021-3.489c0-.168-.042-.316-.042-.316-.127-.885-.822-1.098-1.45-1.077.191-.549.57-1.055 1.219-1.076.57-.001.887.38.908 1.054zm-.952-.042l-.042 3.385c-.253.106-.506.191-.759.297l-.105-2.707c0-.295-.148-.549-.148-.549-.253-.909-.887-.951-1.45-.909.106-.549.549-1.097 1.24-1.097.612.021 1.115.443 1.264 1.58zm-.846.064l-.063 2.792c-.231.106-.485.212-.737.317l-.063-1.981c0-.232-.106-.443-.106-.443-.232-.887-.844-.929-1.344-.887.084-.549.485-1.076 1.177-1.076.592.021 1.073.484 1.136 1.278zm-2.161 2.119l1.009 12.014c.021.212.359.17.529.149l5.208-1.265c.064-.021.127-.042.17-.084l1.009-12.035c.042-.486-.295-.634-.634-.57l-6.7 1.349c-.337.084-.654.232-.591.442z"/>
        </svg>
      );
    }

    if (id.includes('calendar')) {
      return (
        <svg viewBox="0 0 24 24" className={iconClass}>
          <path fill="#4285F4" d="M19.5 3h-3V1.5h-1.5V3h-6V1.5H7.5V3h-3C3.675 3 3 3.675 3 4.5v15c0 .825.675 1.5 1.5 1.5h15c.825 0 1.5-.675 1.5-1.5v-15c0-.825-.675-1.5-1.5-1.5zm0 16.5h-15V8.25h15v11.25z"/>
          <path fill="#4285F4" d="M7.5 10.5h3v3h-3zM10.5 10.5h3v3h-3zM13.5 10.5h3v3h-3zM7.5 13.5h3v3h-3zM10.5 13.5h3v3h-3z"/>
        </svg>
      );
    }

    if (id.includes('drive') || id.includes('docs')) {
      return (
        <svg viewBox="0 0 24 24" className={iconClass}>
          <path fill="#4285F4" d="M7.71 3.5L1.15 15l3.43 6 6.56-11.5z"/>
          <path fill="#FBBC04" d="M22.85 15L16.29 3.5H9.14l6.56 11.5z"/>
          <path fill="#34A853" d="M1.15 15h13.71l3.43 6H4.58z"/>
        </svg>
      );
    }

    // Default fallback - use first letter with color
    return (
      <span className="text-lg font-bold" style={{ color }}>
        {providerName[0].toUpperCase()}
      </span>
    );
  };

  return (
    <div
      className="w-10 h-10 rounded-lg flex items-center justify-center"
      style={{ backgroundColor: connected ? 'rgba(34, 197, 94, 0.2)' : `${color}15` }}
    >
      {getIcon()}
    </div>
  );
};

// Dynamic questions based on business context - different from onboarding
const generateDynamicQuestions = (knowledge: KnowledgeEntry[]) => {
  const hasIndustry = knowledge.some(k => k.key === 'business_industry');
  const hasTeamSize = knowledge.some(k => k.key === 'team_size');
  const hasSocialChannels = knowledge.some(k => k.key === 'social_media_channels');
  const hasRevenue = knowledge.some(k => k.key === 'monthly_revenue');
  const hasFocus = knowledge.some(k => k.key === 'primary_focus');

  const questions = [
    // Brand & Voice questions
    { id: 1, question: "What tone of voice should your AI use? (Professional, Friendly, Casual, Formal)", category: 'voice', priority: 1 },
    { id: 2, question: "What words or phrases should your AI avoid using?", category: 'guidelines', priority: 2 },
    { id: 3, question: "How would you describe your brand personality in 3 words?", category: 'brand', priority: 3 },

    // Customer & Market questions
    { id: 4, question: "What is the biggest pain point your customers face?", category: 'customers', priority: 4 },
    { id: 5, question: "What makes customers choose you over competitors?", category: 'competitive', priority: 5 },
    { id: 6, question: "Who are your top 3 competitors?", category: 'market', priority: 6 },

    // Content & Marketing questions
    { id: 7, question: "What type of content performs best for your audience?", category: 'content', priority: 7 },
    { id: 8, question: "What are your key marketing messages?", category: 'marketing', priority: 8 },
    { id: 9, question: "What hashtags or keywords are important for your business?", category: 'seo', priority: 9 },

    // Operations questions
    { id: 10, question: "What are your business hours and timezone?", category: 'operations', priority: 10 },
    { id: 11, question: "What is your typical response time to customer inquiries?", category: 'service', priority: 11 },
    { id: 12, question: "What are your most frequently asked questions from customers?", category: 'faq', priority: 12 },
  ];

  // Filter out questions that might overlap with existing knowledge
  return questions.filter(q => {
    if (q.category === 'voice' && knowledge.some(k => k.key === 'brand_voice')) return false;
    if (q.category === 'market' && knowledge.some(k => k.key === 'competitors')) return false;
    return true;
  }).slice(0, 10); // Return max 10 questions
};

export default function BrainPage() {
  const { token } = useAuthStore();
  const { theme } = useThemeStore();
  const { currentWorkspace, workspaces, fetchWorkspaces, _hasHydrated } = useWorkspaceStore();
  const isDark = theme === 'dark';

  // Fetch workspaces if not loaded (for dropdown) - but wait for hydration first
  useEffect(() => {
    if (!token || !_hasHydrated) return;

    // Fetch workspaces in background if not loaded (for dropdown)
    if (workspaces.length === 0) {
      fetchWorkspaces(token);
    }
  }, [token, _hasHydrated, workspaces.length, fetchWorkspaces]);

  // State
  const [activeFilter, setActiveFilter] = useState<ContentFilter>('all');
  const [documents, setDocuments] = useState<BrainDocument[]>([]);
  const [stats, setStats] = useState<BrainStats | null>(null);
  const [knowledge, setKnowledge] = useState<KnowledgeEntry[]>([]);
  const [providers, setProviders] = useState<Provider[]>([]);
  const [integrations, setIntegrations] = useState<Integration[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [isUploading, setIsUploading] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const [urlInput, setUrlInput] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [successMessage, setSuccessMessage] = useState<string | null>(null);
  const [showInfoCard, setShowInfoCard] = useState(true);
  const [showAddContent, setShowAddContent] = useState(false);
  const [showQuestionsModal, setShowQuestionsModal] = useState(false);
  const [currentQuestionIndex, setCurrentQuestionIndex] = useState(0);
  const [questionAnswer, setQuestionAnswer] = useState('');
  const [savingAnswer, setSavingAnswer] = useState(false);
  const [connecting, setConnecting] = useState(false);
  const [showKnowledgeModal, setShowKnowledgeModal] = useState(false);
  const [selectedKnowledge, setSelectedKnowledge] = useState<KnowledgeEntry | null>(null);
  const [editedContent, setEditedContent] = useState('');
  const [isEditing, setIsEditing] = useState(false);
  const [personalUseOnly, setPersonalUseOnly] = useState(false);
  const [savingKnowledge, setSavingKnowledge] = useState(false);
  const [deletingKnowledge, setDeletingKnowledge] = useState(false);
  const [showIntegrationsModal, setShowIntegrationsModal] = useState(false);
  const [showAnalyzeWebsiteModal, setShowAnalyzeWebsiteModal] = useState(false);
  const [websiteUrlInput, setWebsiteUrlInput] = useState('');
  const [analyzingWebsite, setAnalyzingWebsite] = useState(false);
  const [pendingUrls, setPendingUrls] = useState<string[]>([]);
  const [urlInputForList, setUrlInputForList] = useState('');
  const [addingUrls, setAddingUrls] = useState(false);
  const [analyzeAsBrand, setAnalyzeAsBrand] = useState(false);
  const fileInputRef = useRef<HTMLInputElement>(null);
  const contentEditRef = useRef<HTMLDivElement>(null);

  // Get accepted file types based on active filter
  const getAcceptedFileTypes = () => {
    switch (activeFilter) {
      case 'images':
        return '.png,.jpg,.jpeg,.gif,.webp,.svg,.bmp,.tiff';
      case 'videos':
        return '.mp4,.webm,.ogg,.mov,.avi,.wmv,.mkv';
      case 'documents':
        return '.pdf,.txt,.csv,.doc,.docx,.md,.json,.html,.xml,.xls,.xlsx';
      default:
        return '.pdf,.txt,.csv,.doc,.docx,.md,.json,.html,.xml,.xls,.xlsx,.png,.jpg,.jpeg,.gif,.webp,.mp4,.webm,.mov';
    }
  };

  // Get upload label based on active filter
  const getUploadLabel = () => {
    switch (activeFilter) {
      case 'images':
        return { title: 'Upload Images', hint: 'PNG, JPG, GIF, WebP (max 50MB)' };
      case 'videos':
        return { title: 'Upload Videos', hint: 'MP4, WebM, MOV, AVI (max 50MB)' };
      case 'documents':
        return { title: 'Upload Documents', hint: 'PDF, DOC, TXT, CSV, XLS (max 50MB)' };
      default:
        return { title: 'Upload Files', hint: 'PDF, DOC, TXT, Images, Videos (max 50MB)' };
    }
  };

  // Check if URL input should be shown
  const showUrlInput = activeFilter === 'all' || activeFilter === 'memories' || activeFilter === 'webpages';

  // Check if file upload should be shown
  const showFileUpload = activeFilter !== 'webpages';

  // Check localStorage for info card dismissal
  useEffect(() => {
    const dismissed = localStorage.getItem('brain_info_dismissed');
    if (dismissed === 'true') {
      setShowInfoCard(false);
    }
  }, []);

  // Fetch all data
  const fetchData = useCallback(async () => {
    if (!token || !_hasHydrated) return;

    // Build workspace query param - always include workspace_id when available
    const workspaceId = currentWorkspace?.id;
    const wsParam = workspaceId ? `?workspace_id=${workspaceId}` : '';
    const baseHeaders: HeadersInit = { Authorization: `Bearer ${token}` };
    const wsHeaders: HeadersInit = workspaceId
      ? { Authorization: `Bearer ${token}`, 'X-Workspace-Id': String(workspaceId) }
      : baseHeaders;

    console.log('[Brain] Fetching data with workspace_id:', workspaceId, 'hydrated:', _hasHydrated);

    try {
      const [docsRes, statsRes, providersRes, integrationsRes, knowledgeRes] = await Promise.all([
        fetch(`${API_URL}/brain/documents${wsParam}`, {
          headers: wsHeaders,
        }),
        fetch(`${API_URL}/brain/stats${wsParam}`, {
          headers: wsHeaders,
        }),
        fetch(`${API_URL}/integrations/providers`, {
          headers: baseHeaders,
        }),
        fetch(`${API_URL}/integrations${wsParam}`, {
          headers: wsHeaders,
        }),
        fetch(`${API_URL}/brain/knowledge${wsParam}`, {
          headers: wsHeaders,
        }),
      ]);

      if (docsRes.ok) {
        const docsData = await docsRes.json() as { success: boolean; data: { items: BrainDocument[] } };
        if (docsData.success) {
          setDocuments(docsData.data.items);
        }
      }

      if (statsRes.ok) {
        const statsData = await statsRes.json() as { success: boolean; data: BrainStats };
        if (statsData.success) {
          setStats(statsData.data);
        }
      }

      if (providersRes.ok) {
        const providersData = await providersRes.json() as { success: boolean; data: { providers: Provider[] } };
        if (providersData.success && providersData.data) {
          // Show all providers, including 'Coming Soon' ones
          setProviders(providersData.data.providers);
        }
      }

      if (integrationsRes.ok) {
        const intData = await integrationsRes.json() as { success: boolean; data: Integration[] };
        if (intData.success) {
          setIntegrations(intData.data.filter(i => i.status === 'connected'));
        }
      }

      if (knowledgeRes.ok) {
        const knowledgeData = await knowledgeRes.json() as { success: boolean; data: KnowledgeEntry[] };
        if (knowledgeData.success) {
          setKnowledge(knowledgeData.data);
        }
      }
    } catch (err) {
      console.error('Failed to fetch brain data:', err);
    } finally {
      setIsLoading(false);
    }
  }, [token, currentWorkspace?.id, _hasHydrated]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  // Poll for processing documents
  useEffect(() => {
    const hasProcessing = documents.some((d) => d.status === 'processing');
    if (!hasProcessing) return;

    const interval = setInterval(fetchData, 3000);
    return () => clearInterval(interval);
  }, [documents, fetchData]);

  // Get dynamic questions based on existing knowledge
  const dynamicQuestions = generateDynamicQuestions(knowledge);
  const currentQuestion = dynamicQuestions[currentQuestionIndex];
  const totalQuestions = dynamicQuestions.length;

  // Dismiss info card
  const handleDismissInfoCard = () => {
    setShowInfoCard(false);
    localStorage.setItem('brain_info_dismissed', 'true');
  };

  // Save answer to knowledge
  const handleSaveAnswer = async () => {
    if (!questionAnswer.trim() || !currentQuestion || !token) return;

    setSavingAnswer(true);
    const headers: HeadersInit = {
      Authorization: `Bearer ${token}`,
      'Content-Type': 'application/json',
    };
    if (currentWorkspace?.id) {
      (headers as Record<string, string>)['X-Workspace-Id'] = String(currentWorkspace.id);
    }
    try {
      const response = await fetch(`${API_URL}/brain/knowledge`, {
        method: 'POST',
        headers,
        body: JSON.stringify({
          key: `question_${currentQuestion.category}_${currentQuestion.id}`,
          label: currentQuestion.question,
          value: questionAnswer.trim(),
          category: currentQuestion.category,
          source: 'questions',
          workspace_id: currentWorkspace?.id || null,
        }),
      });

      if (response.ok) {
        setQuestionAnswer('');
        if (currentQuestionIndex < totalQuestions - 1) {
          setCurrentQuestionIndex(currentQuestionIndex + 1);
        } else {
          setShowQuestionsModal(false);
          setCurrentQuestionIndex(0);
          setSuccessMessage('All questions answered! Your AI is now smarter.');
          setTimeout(() => setSuccessMessage(null), 3000);
          fetchData();
        }
      }
    } catch (err) {
      setError('Failed to save answer');
    } finally {
      setSavingAnswer(false);
    }
  };

  // Skip current question
  const handleSkipQuestion = () => {
    if (currentQuestionIndex < totalQuestions - 1) {
      setCurrentQuestionIndex(currentQuestionIndex + 1);
      setQuestionAnswer('');
    } else {
      setShowQuestionsModal(false);
      setCurrentQuestionIndex(0);
      setQuestionAnswer('');
    }
  };

  // Open knowledge view modal
  const handleViewKnowledge = (entry: KnowledgeEntry) => {
    setSelectedKnowledge(entry);
    setEditedContent(entry.value);
    setIsEditing(false);
    setPersonalUseOnly(false);
    setShowKnowledgeModal(true);
  };

  // Rich text formatting
  const applyFormatting = (format: string) => {
    if (!contentEditRef.current) return;

    const selection = window.getSelection();
    if (!selection || selection.rangeCount === 0) return;

    const range = selection.getRangeAt(0);
    const selectedText = range.toString();

    let formattedText = selectedText;

    switch (format) {
      case 'bold':
        formattedText = `**${selectedText}**`;
        break;
      case 'italic':
        formattedText = `*${selectedText}*`;
        break;
      case 'strikethrough':
        formattedText = `~~${selectedText}~~`;
        break;
      case 'h1':
        formattedText = `# ${selectedText}`;
        break;
      case 'h2':
        formattedText = `## ${selectedText}`;
        break;
      case 'h3':
        formattedText = `### ${selectedText}`;
        break;
      case 'bullet':
        formattedText = `• ${selectedText}`;
        break;
      case 'numbered':
        formattedText = `1. ${selectedText}`;
        break;
      case 'quote':
        formattedText = `> ${selectedText}`;
        break;
      case 'hr':
        formattedText = `${selectedText}\n---\n`;
        break;
    }

    // Update the content
    const currentContent = editedContent;
    const start = currentContent.indexOf(selectedText);
    if (start !== -1) {
      const newContent = currentContent.substring(0, start) + formattedText + currentContent.substring(start + selectedText.length);
      setEditedContent(newContent);
    }
  };

  // Save knowledge changes
  const handleSaveKnowledge = async () => {
    if (!selectedKnowledge || !token) return;

    setSavingKnowledge(true);
    const headers: HeadersInit = {
      Authorization: `Bearer ${token}`,
      'Content-Type': 'application/json',
    };
    if (currentWorkspace?.id) {
      (headers as Record<string, string>)['X-Workspace-Id'] = String(currentWorkspace.id);
    }

    const wsParam = currentWorkspace?.id ? `?workspace_id=${currentWorkspace.id}` : '';

    try {
      const response = await fetch(`${API_URL}/brain/knowledge/${selectedKnowledge.id}${wsParam}`, {
        method: 'PUT',
        headers,
        body: JSON.stringify({
          value: editedContent,
          personal_use_only: personalUseOnly,
        }),
      });

      if (response.ok) {
        setSuccessMessage('Knowledge updated successfully');
        setTimeout(() => setSuccessMessage(null), 3000);
        setShowKnowledgeModal(false);
        setSelectedKnowledge(null);
        setIsEditing(false);
        fetchData();
      } else {
        setError('Failed to update knowledge');
      }
    } catch (err) {
      setError('Failed to save changes');
    } finally {
      setSavingKnowledge(false);
    }
  };

  // Delete knowledge entry
  const handleDeleteKnowledge = async () => {
    if (!selectedKnowledge || !token) return;
    if (!confirm('Are you sure you want to delete this knowledge entry?')) return;

    setDeletingKnowledge(true);
    const headers: HeadersInit = { Authorization: `Bearer ${token}` };
    if (currentWorkspace?.id) {
      (headers as Record<string, string>)['X-Workspace-Id'] = String(currentWorkspace.id);
    }

    const wsParam = currentWorkspace?.id ? `?workspace_id=${currentWorkspace.id}` : '';

    try {
      const response = await fetch(`${API_URL}/brain/knowledge/${selectedKnowledge.id}${wsParam}`, {
        method: 'DELETE',
        headers,
      });

      if (response.ok) {
        setSuccessMessage('Knowledge deleted successfully');
        setTimeout(() => setSuccessMessage(null), 3000);
        setShowKnowledgeModal(false);
        setSelectedKnowledge(null);
        fetchData();
      } else {
        setError('Failed to delete knowledge');
      }
    } catch (err) {
      setError('Failed to delete knowledge');
    } finally {
      setDeletingKnowledge(false);
    }
  };

  // Analyze website for brand information
  const handleAnalyzeWebsite = async () => {
    if (!websiteUrlInput.trim() || !token) return;

    // Validate URL
    try {
      new URL(websiteUrlInput);
    } catch {
      setError('Please enter a valid URL (e.g., https://example.com)');
      return;
    }

    setAnalyzingWebsite(true);
    setError(null);

    const headers: HeadersInit = {
      'Authorization': `Bearer ${token}`,
      'Content-Type': 'application/json',
    };
    if (currentWorkspace?.id) {
      (headers as Record<string, string>)['X-Workspace-Id'] = String(currentWorkspace.id);
    }

    const wsParam = currentWorkspace?.id ? `?workspace_id=${currentWorkspace.id}` : '';

    try {
      const response = await fetch(`${API_URL}/brain/analyze-website${wsParam}`, {
        method: 'POST',
        headers,
        body: JSON.stringify({ url: websiteUrlInput }),
      });

      const data = await response.json() as { success: boolean; error?: string; message?: string };

      if (response.ok && data.success) {
        setSuccessMessage(data.message || 'Website analyzed successfully! Brand information has been saved.');
        setTimeout(() => setSuccessMessage(null), 5000);
        setShowAnalyzeWebsiteModal(false);
        setWebsiteUrlInput('');
        fetchData(); // Refresh to show new brand knowledge
      } else {
        setError(data.error || 'Failed to analyze website');
      }
    } catch (err) {
      setError('Failed to analyze website. Please check the URL and try again.');
    } finally {
      setAnalyzingWebsite(false);
    }
  };

  // Add URL to pending list
  const handleAddUrlToList = () => {
    if (!urlInputForList.trim()) return;

    try {
      new URL(urlInputForList);
    } catch {
      setError('Please enter a valid URL (e.g., https://example.com)');
      return;
    }

    if (pendingUrls.includes(urlInputForList.trim())) {
      setError('This URL is already in the list');
      return;
    }

    setPendingUrls([...pendingUrls, urlInputForList.trim()]);
    setUrlInputForList('');
    setError(null);
  };

  // Remove URL from pending list
  const handleRemoveUrlFromList = (url: string) => {
    setPendingUrls(pendingUrls.filter(u => u !== url));
  };

  // Add all pending URLs (with optional brand analysis for first URL)
  const handleAddAllUrls = async () => {
    if (pendingUrls.length === 0 || !token) return;

    setAddingUrls(true);
    setError(null);

    const headers: HeadersInit = {
      'Authorization': `Bearer ${token}`,
      'Content-Type': 'application/json',
    };
    if (currentWorkspace?.id) {
      (headers as Record<string, string>)['X-Workspace-Id'] = String(currentWorkspace.id);
    }

    const wsParam = currentWorkspace?.id ? `?workspace_id=${currentWorkspace.id}` : '';
    let successCount = 0;
    let errorCount = 0;

    for (let i = 0; i < pendingUrls.length; i++) {
      const url = pendingUrls[i];
      const isFirstUrl = i === 0;

      try {
        // If analyzeAsBrand is enabled and this is the first URL, use brand analysis
        if (analyzeAsBrand && isFirstUrl) {
          const response = await fetch(`${API_URL}/brain/analyze-website${wsParam}`, {
            method: 'POST',
            headers,
            body: JSON.stringify({ url }),
          });

          const data = await response.json() as { success: boolean; error?: string };
          if (response.ok && data.success) {
            successCount++;
          } else {
            errorCount++;
          }
        } else {
          // Regular URL add (as document)
          const response = await fetch(`${API_URL}/brain/documents`, {
            method: 'POST',
            headers,
            body: JSON.stringify({
              source_url: url,
              workspace_id: currentWorkspace?.id,
            }),
          });

          const data = await response.json() as { success: boolean; error?: string };
          if (response.ok && data.success) {
            successCount++;
          } else {
            errorCount++;
          }
        }
      } catch (err) {
        errorCount++;
      }
    }

    if (successCount > 0) {
      const brandMsg = analyzeAsBrand ? ' Brand information extracted from the first URL.' : '';
      setSuccessMessage(`${successCount} webpage${successCount > 1 ? 's' : ''} added successfully!${brandMsg}`);
      setTimeout(() => setSuccessMessage(null), 5000);
    }

    if (errorCount > 0) {
      setError(`${errorCount} webpage${errorCount > 1 ? 's' : ''} failed to add.`);
    }

    setPendingUrls([]);
    setAnalyzeAsBrand(false);
    setShowAnalyzeWebsiteModal(false);
    fetchData();
    setAddingUrls(false);
  };

  // File upload handler with filter-based validation
  const handleFileUpload = async (files: FileList | null) => {
    if (!files || files.length === 0 || !token) return;

    // Validate file types based on active filter
    const imageExtensions = ['.png', '.jpg', '.jpeg', '.gif', '.webp', '.svg', '.bmp', '.tiff'];
    const videoExtensions = ['.mp4', '.webm', '.ogg', '.mov', '.avi', '.wmv', '.mkv'];
    const documentExtensions = ['.pdf', '.txt', '.csv', '.doc', '.docx', '.md', '.json', '.html', '.xml', '.xls', '.xlsx'];

    const filesArray = Array.from(files);
    for (const file of filesArray) {
      const ext = '.' + file.name.split('.').pop()?.toLowerCase();

      if (activeFilter === 'images' && !imageExtensions.includes(ext)) {
        setError(`Invalid file type for Images: ${file.name}. Please upload PNG, JPG, GIF, or WebP files.`);
        return;
      }
      if (activeFilter === 'videos' && !videoExtensions.includes(ext)) {
        setError(`Invalid file type for Videos: ${file.name}. Please upload MP4, WebM, or MOV files.`);
        return;
      }
      if (activeFilter === 'documents' && !documentExtensions.includes(ext)) {
        setError(`Invalid file type for Documents: ${file.name}. Please upload PDF, DOC, TXT, or CSV files.`);
        return;
      }
    }

    setIsUploading(true);
    setError(null);

    const uploadHeaders: HeadersInit = { Authorization: `Bearer ${token}` };
    if (currentWorkspace?.id) {
      (uploadHeaders as Record<string, string>)['X-Workspace-Id'] = String(currentWorkspace.id);
    }

    try {
      for (const file of filesArray) {
        const formData = new FormData();
        formData.append('file', file);
        if (currentWorkspace?.id) {
          formData.append('workspace_id', String(currentWorkspace.id));
        }

        const response = await fetch(`${API_URL}/brain/documents`, {
          method: 'POST',
          headers: uploadHeaders,
          body: formData,
        });

        const data = await response.json() as { success: boolean; error?: string };

        if (!response.ok) {
          throw new Error(data.error || 'Upload failed');
        }
      }

      // Dynamic success message based on filter
      const uploadType = activeFilter === 'images' ? 'Image(s)' : activeFilter === 'videos' ? 'Video(s)' : 'File(s)';
      setSuccessMessage(`${uploadType} uploaded successfully!`);
      setTimeout(() => setSuccessMessage(null), 3000);
      notify.success(`${uploadType} Uploaded`, 'Your content has been added.', '/dashboard/brain');
      fetchData();
      setShowAddContent(false);
    } catch (err) {
      const errorMsg = err instanceof Error ? err.message : 'Upload failed';
      setError(errorMsg);
      notify.error('Upload Failed', errorMsg);
    } finally {
      setIsUploading(false);
      if (fileInputRef.current) {
        fileInputRef.current.value = '';
      }
    }
  };

  // Add URL handler
  const handleAddUrl = async () => {
    if (!urlInput.trim() || !token) return;

    setIsUploading(true);
    setError(null);

    const urlHeaders: HeadersInit = {
      Authorization: `Bearer ${token}`,
      'Content-Type': 'application/json',
    };
    if (currentWorkspace?.id) {
      (urlHeaders as Record<string, string>)['X-Workspace-Id'] = String(currentWorkspace.id);
    }

    try {
      const response = await fetch(`${API_URL}/brain/documents`, {
        method: 'POST',
        headers: urlHeaders,
        body: JSON.stringify({
          source_url: urlInput,
          workspace_id: currentWorkspace?.id || null,
        }),
      });

      const data = await response.json() as { success: boolean; error?: string };

      if (!response.ok) {
        throw new Error(data.error || 'Failed to add URL');
      }

      setUrlInput('');
      setSuccessMessage('URL added successfully!');
      setTimeout(() => setSuccessMessage(null), 3000);
      notify.success('URL Added', 'The website content is being crawled.', '/dashboard/brain');
      fetchData();
      setShowAddContent(false);
    } catch (err) {
      const errorMsg = err instanceof Error ? err.message : 'Failed to add URL';
      setError(errorMsg);
      notify.error('URL Failed', errorMsg);
    } finally {
      setIsUploading(false);
    }
  };

  // Delete document
  const handleDeleteDocument = async (documentId: number) => {
    if (!token) return;
    if (!confirm('Are you sure you want to delete this?')) return;

    const deleteHeaders: HeadersInit = { Authorization: `Bearer ${token}` };
    if (currentWorkspace?.id) {
      (deleteHeaders as Record<string, string>)['X-Workspace-Id'] = String(currentWorkspace.id);
    }

    const wsParam = currentWorkspace?.id ? `?workspace_id=${currentWorkspace.id}` : '';

    try {
      const response = await fetch(`${API_URL}/brain/documents/${documentId}${wsParam}`, {
        method: 'DELETE',
        headers: deleteHeaders,
      });

      if (!response.ok) {
        throw new Error('Failed to delete');
      }

      setSuccessMessage('Deleted successfully');
      setTimeout(() => setSuccessMessage(null), 3000);
      fetchData();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Delete failed');
    }
  };

  // Connect integration
  const connectProvider = async (provider: Provider) => {
    if (!token) return;
    setConnecting(true);
    setError(null);

    try {
      const res = await fetch(`${API_URL}/integrations/connect/${provider.id}`, {
        method: 'POST',
        headers: {
          Authorization: `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          workspace_id: currentWorkspace?.id || null,
        }),
      });

      interface ConnectResponse {
        success: boolean;
        data?: {
          requires_setup?: boolean;
          message?: string;
          auth_url?: string;
        };
        error?: string;
      }
      const data = await res.json() as ConnectResponse;

      if (data.success) {
        if (data.data?.requires_setup) {
          setError(data.data.message || 'Setup required');
        } else if (data.data?.auth_url) {
          window.location.href = data.data.auth_url;
        }
      } else {
        setError(data.error || 'Failed to connect');
      }
    } catch {
      setError('Failed to initiate connection');
    }
    setConnecting(false);
  };

  // Disconnect integration
  const disconnectIntegration = async (integrationId: number) => {
    if (!token) return;

    try {
      const wsParam = currentWorkspace?.id ? `?workspace_id=${currentWorkspace.id}` : '';
      const wsHeaders: HeadersInit = currentWorkspace?.id
        ? { Authorization: `Bearer ${token}`, 'X-Workspace-Id': String(currentWorkspace.id) }
        : { Authorization: `Bearer ${token}` };

      const res = await fetch(`${API_URL}/integrations/${integrationId}`, {
        method: 'DELETE',
        headers: wsHeaders,
      });

      interface DisconnectResponse {
        success: boolean;
        error?: string;
      }
      const data = await res.json() as DisconnectResponse;

      if (data.success) {
        // Refresh integrations list with workspace isolation
        const integrationsRes = await fetch(`${API_URL}/integrations${wsParam}`, {
          headers: wsHeaders,
        });
        const integrationsData = await integrationsRes.json() as { success: boolean; data?: Integration[] };
        if (integrationsData.success && integrationsData.data) {
          setIntegrations(integrationsData.data.filter(i => i.status === 'connected'));
        }
      } else {
        setError(data.error || 'Failed to disconnect');
      }
    } catch {
      setError('Failed to disconnect integration');
    }
  };

  // Filter documents based on type
  const getFilteredDocuments = () => {
    let filtered = documents;

    switch (activeFilter) {
      case 'documents':
        filtered = documents.filter(d => ['pdf', 'doc', 'docx', 'txt', 'md', 'csv', 'xls', 'xlsx'].includes(d.type.toLowerCase()));
        break;
      case 'webpages':
        filtered = documents.filter(d => d.type === 'url' || d.source_url);
        break;
      case 'images':
        filtered = documents.filter(d => ['png', 'jpg', 'jpeg', 'gif', 'webp', 'svg'].includes(d.type.toLowerCase()));
        break;
      case 'videos':
        filtered = documents.filter(d => ['mp4', 'webm', 'mov', 'avi'].includes(d.type.toLowerCase()));
        break;
      case 'memories':
        filtered = documents.filter(d => d.name.toLowerCase().includes('business information') || d.name.toLowerCase().includes('memory'));
        break;
    }

    if (searchQuery) {
      filtered = filtered.filter(d =>
        d.name.toLowerCase().includes(searchQuery.toLowerCase()) ||
        d.content_summary?.toLowerCase().includes(searchQuery.toLowerCase())
      );
    }

    return filtered;
  };

  const formatDate = (dateString: string) => {
    let date: Date;
    if (dateString.includes('T')) {
      date = new Date(dateString);
    } else {
      date = new Date(dateString.replace(' ', 'T') + 'Z');
    }

    if (isNaN(date.getTime())) return 'Unknown';

    const now = new Date();
    const diff = now.getTime() - date.getTime();
    const minutes = Math.floor(diff / (1000 * 60));
    const hours = Math.floor(diff / (1000 * 60 * 60));
    const days = Math.floor(hours / 24);

    if (minutes < 1) return 'Just now';
    if (minutes < 60) return `${minutes}m ago`;
    if (hours < 24) return `${hours}h ago`;
    if (days < 7) return `${days}d ago`;
    return date.toLocaleDateString();
  };

  const getDocIcon = (type: string) => {
    const t = type.toLowerCase();
    if (['pdf', 'doc', 'docx', 'txt', 'md'].includes(t)) return FileText;
    if (['png', 'jpg', 'jpeg', 'gif', 'webp', 'svg'].includes(t)) return FileImage;
    if (['mp4', 'webm', 'mov', 'avi'].includes(t)) return FileVideo;
    if (t === 'url') return Globe;
    return File;
  };

  const connectedProviderIds = new Set(integrations.map(i => i.provider));
  const filteredDocs = getFilteredDocuments();
  const availableProviders = providers.filter(p => !connectedProviderIds.has(p.id));

  // Count documents by type
  const typeCounts = {
    all: documents.length,
    memories: documents.filter(d => d.name.toLowerCase().includes('business information') || d.name.toLowerCase().includes('memory')).length,
    images: documents.filter(d => ['png', 'jpg', 'jpeg', 'gif', 'webp', 'svg'].includes(d.type.toLowerCase())).length,
    videos: documents.filter(d => ['mp4', 'webm', 'mov', 'avi'].includes(d.type.toLowerCase())).length,
    webpages: documents.filter(d => d.type === 'url' || d.source_url).length,
    documents: documents.filter(d => ['pdf', 'doc', 'docx', 'txt', 'md', 'csv', 'xls', 'xlsx'].includes(d.type.toLowerCase())).length,
  };

  if (isLoading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-accent-purple"></div>
      </div>
    );
  }

  return (
    <motion.div
      initial="hidden"
      animate="visible"
      variants={{ visible: { transition: { staggerChildren: 0.1 } } }}
      className="space-y-6 max-w-5xl mx-auto"
    >
      {/* Header */}
      <motion.div variants={fadeInUp} className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-3">
        <h1 className={`text-2xl sm:text-3xl font-bold ${isDark ? 'text-white' : 'text-slate-900'}`}>Brain AI</h1>
        <button
          onClick={() => setShowAddContent(true)}
          className="btn-primary flex items-center gap-2 w-full sm:w-auto justify-center"
        >
          <Plus className="w-4 h-4" />
          Add knowledge
        </button>
      </motion.div>

      {/* How Brain AI Works - Info Card */}
      <AnimatePresence>
        {showInfoCard && (
          <motion.div
            initial={{ opacity: 0, y: -10 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, height: 0, marginBottom: 0 }}
            className={`rounded-2xl border p-4 sm:p-6 ${isDark ? 'bg-gradient-to-r from-accent-purple/10 to-accent-cyan/10 border-accent-purple/20' : 'bg-gradient-to-r from-purple-50 to-cyan-50 border-purple-200'}`}
          >
            <div className="flex flex-col sm:flex-row items-center sm:items-start gap-4 sm:gap-6 text-center sm:text-left">
              <div className="w-16 h-16 sm:w-24 sm:h-24 flex-shrink-0 rounded-2xl bg-gradient-accent flex items-center justify-center">
                <Brain className="w-8 h-8 sm:w-12 sm:h-12 text-white" />
              </div>
              <div className="flex-1">
                <h3 className={`text-lg sm:text-xl font-semibold mb-2 ${isDark ? 'text-white' : 'text-slate-900'}`}>
                  How Brain AI works
                </h3>
                <p className={`text-sm sm:text-base mb-2 ${isDark ? 'text-text-secondary' : 'text-slate-600'}`}>
                  Think of Brain AI as your digital brain. Add your brand details, links, and files, and it will use them to understand you better.
                </p>
                <p className={`text-sm sm:text-base ${isDark ? 'text-text-secondary' : 'text-slate-600'}`}>
                  The richer the info, the smarter and more helpful your AI becomes.
                </p>
              </div>
              <button
                onClick={handleDismissInfoCard}
                className="btn-primary px-6 w-full sm:w-auto"
              >
                Ok, got it!
              </button>
            </div>
          </motion.div>
        )}
      </AnimatePresence>

      {/* Search & Filter Section */}
      <motion.div variants={fadeInUp} className="flex flex-col sm:flex-row items-stretch sm:items-center gap-4">
        {/* Search */}
        <div className="relative flex-shrink-0">
          <Search className={`absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 ${isDark ? 'text-text-muted' : 'text-slate-400'}`} />
          <input
            type="text"
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            placeholder="Search"
            className={`w-full sm:w-40 pl-10 pr-4 py-2.5 rounded-xl border text-sm focus:outline-none focus:border-accent-purple ${isDark ? 'bg-dark-800 border-border text-white placeholder-text-muted' : 'bg-white border-slate-200 text-slate-900 placeholder-slate-400'}`}
          />
        </div>

        {/* Filter Tabs */}
        <div className="flex items-center gap-2 overflow-x-auto pb-1 scrollbar-hide">
          {[
            { id: 'all' as ContentFilter, label: 'All', icon: Brain },
            { id: 'memories' as ContentFilter, label: 'Memories', icon: Lightbulb },
            { id: 'images' as ContentFilter, label: 'Images', icon: Image },
            { id: 'videos' as ContentFilter, label: 'Videos', icon: Video },
            { id: 'webpages' as ContentFilter, label: 'Webpages', icon: Globe },
            { id: 'documents' as ContentFilter, label: 'Documents', icon: FileText },
          ].map((tab) => (
            <button
              key={tab.id}
              onClick={() => setActiveFilter(tab.id)}
              className={`flex items-center gap-2 px-4 py-2 rounded-full text-sm font-medium whitespace-nowrap transition-all ${
                activeFilter === tab.id
                  ? 'bg-accent-cyan text-white'
                  : isDark ? 'bg-dark-700 text-text-secondary hover:text-white hover:bg-dark-600' : 'bg-slate-100 text-slate-600 hover:text-slate-900 hover:bg-slate-200'
              }`}
            >
              <tab.icon className="w-4 h-4" />
              {tab.label}
            </button>
          ))}
        </div>
      </motion.div>

      {/* Error/Success Messages */}
      <AnimatePresence>
        {(error || successMessage) && (
          <motion.div
            initial={{ opacity: 0, y: -10 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0 }}
            className={`p-4 rounded-xl flex items-center justify-between ${
              error ? 'bg-accent-red/10 border border-accent-red/20' : 'bg-accent-green/10 border border-accent-green/20'
            }`}
          >
            <div className="flex items-center space-x-3">
              {error ? <AlertCircle className="w-5 h-5 text-accent-red" /> : <Check className="w-5 h-5 text-accent-green" />}
              <span className={error ? 'text-accent-red' : 'text-accent-green'}>{error || successMessage}</span>
            </div>
            <button onClick={() => { setError(null); setSuccessMessage(null); }}>
              <X className={`w-5 h-5 ${error ? 'text-accent-red' : 'text-accent-green'}`} />
            </button>
          </motion.div>
        )}
      </AnimatePresence>

      {/* Help your AI get smarter - Single line */}
      <motion.div
        variants={fadeInUp}
        className={`rounded-2xl border p-4 flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4 ${isDark ? 'bg-dark-800 border-border' : 'bg-white border-slate-200 shadow-sm'}`}
      >
        <div className="flex items-center gap-3 sm:gap-4">
          <div className="w-10 h-10 rounded-full bg-accent-cyan flex items-center justify-center text-white font-bold flex-shrink-0">
            {totalQuestions}
          </div>
          <div>
            <h3 className={`font-semibold text-sm sm:text-base ${isDark ? 'text-white' : 'text-slate-900'}`}>Help your AI get smarter</h3>
            <p className={`text-xs sm:text-sm ${isDark ? 'text-text-secondary' : 'text-slate-500'}`}>
              Answer {totalQuestions} quick questions to make it work better for you
            </p>
          </div>
        </div>
        <button
          onClick={() => {
            setCurrentQuestionIndex(0);
            setQuestionAnswer('');
            setShowQuestionsModal(true);
          }}
          className="btn-primary flex items-center gap-2 w-full sm:w-auto justify-center text-sm sm:text-base"
        >
          Answer Questions
          <ArrowRight className="w-4 h-4" />
        </button>
      </motion.div>

      {/* Integrations Section - Only show for 'all' filter */}
      {activeFilter === 'all' && (
      <motion.div variants={fadeInUp}>
        <h2 className={`text-lg font-semibold mb-4 ${isDark ? 'text-white' : 'text-slate-900'}`}>Integrations</h2>
        <div className={`rounded-2xl border overflow-hidden ${isDark ? 'bg-dark-800 border-border' : 'bg-white border-slate-200 shadow-sm'}`}>
          <div className={`grid grid-cols-1 lg:grid-cols-2 divide-y lg:divide-y-0 lg:divide-x ${isDark ? 'divide-border' : 'divide-slate-200'}`}>
            {/* Left side - Connect apps */}
            <div className="p-4 sm:p-6 flex flex-col items-center justify-center text-center">
              <div className="flex flex-wrap items-center justify-center gap-2 mb-4">
                {/* Google Calendar */}
                <div className={`w-10 h-10 rounded-lg flex items-center justify-center ${isDark ? 'bg-dark-700' : 'bg-slate-100'}`}>
                  <svg viewBox="0 0 24 24" className="w-6 h-6">
                    <path fill="#4285F4" d="M22 12c0-5.52-4.48-10-10-10S2 6.48 2 12s4.48 10 10 10 10-4.48 10-10z"/>
                    <path fill="#fff" d="M12 6v6l4 2"/>
                    <rect x="6" y="6" width="12" height="12" rx="2" fill="#fff"/>
                    <text x="12" y="15" textAnchor="middle" fill="#4285F4" fontSize="8" fontWeight="bold">31</text>
                  </svg>
                </div>
                {/* Google Drive */}
                <div className={`w-10 h-10 rounded-lg flex items-center justify-center ${isDark ? 'bg-dark-700' : 'bg-slate-100'}`}>
                  <svg viewBox="0 0 24 24" className="w-6 h-6">
                    <path fill="#4285F4" d="M7.71 3.5L1.15 15l3.43 6 6.56-11.5z"/>
                    <path fill="#FBBC04" d="M22.85 15L16.29 3.5H9.14l6.56 11.5z"/>
                    <path fill="#34A853" d="M1.15 15h13.71l3.43 6H4.58z"/>
                  </svg>
                </div>
                {/* Notion */}
                <div className={`w-10 h-10 rounded-lg flex items-center justify-center ${isDark ? 'bg-dark-700' : 'bg-slate-100'}`}>
                  <svg viewBox="0 0 24 24" className="w-6 h-6">
                    <path fill={isDark ? '#fff' : '#000'} d="M4.459 4.208c.746.606 1.026.56 2.428.466l13.215-.793c.28 0 .047-.28-.046-.326L17.86 2.04c-.42-.326-.98-.7-2.055-.607L3.01 2.54c-.466.046-.56.28-.374.466l1.823 1.202zm.793 3.08v13.904c0 .747.373 1.027 1.214.98l14.523-.84c.841-.046.935-.56.935-1.167V6.354c0-.606-.233-.933-.748-.886l-15.177.887c-.56.047-.747.327-.747.933zm14.337.745c.093.42 0 .84-.42.888l-.7.14v10.264c-.608.327-1.168.514-1.635.514-.748 0-.935-.234-1.495-.933l-4.577-7.186v6.952l1.449.327s0 .84-1.168.84l-3.222.186c-.093-.186 0-.653.327-.746l.84-.233V9.854L7.822 9.76c-.094-.42.14-1.026.793-1.073l3.456-.233 4.764 7.279v-6.44l-1.215-.14c-.093-.514.28-.886.747-.933l3.222-.186zM2.332.933L16.24 0c1.68-.14 2.1.093 2.8.606l3.876 2.754c.467.327.607.42.607.98v17.5c0 1.12-.42 1.773-1.869 1.866l-15.457.934c-1.12.046-1.68-.14-2.24-.84L.933 21.467c-.467-.607-.654-1.073-.654-1.773V2.613c0-.886.42-1.586 2.053-1.68z"/>
                  </svg>
                </div>
                {/* Outlook */}
                <div className={`w-10 h-10 rounded-lg flex items-center justify-center ${isDark ? 'bg-dark-700' : 'bg-slate-100'}`}>
                  <svg viewBox="0 0 24 24" className="w-6 h-6">
                    <path fill="#0078D4" d="M24 7.387v10.478c0 .23-.08.424-.238.576-.16.154-.352.232-.576.232h-8.234v-6.18l1.3 1.012c.083.07.187.106.312.106.125 0 .229-.035.312-.106l6.97-5.454c.067-.06.123-.104.168-.13a.272.272 0 0 1 .143-.04c.09 0 .164.035.22.104.057.07.086.15.086.24v.39c0 .094-.03.177-.09.252l-7.064 5.565a.55.55 0 0 1-.34.117.55.55 0 0 1-.34-.117l-1.627-1.27v6.11H.814c-.224 0-.416-.078-.576-.232A.774.774 0 0 1 0 17.864V7.387l.238-.576L.576 6.3H14.952v.51l.576.577h7.658c.224 0 .416.078.576.232.158.153.238.347.238.576v-.002l-.001.195z"/>
                    <path fill="#0078D4" d="M7.5 10.5c-1.38 0-2.5 1.12-2.5 2.5s1.12 2.5 2.5 2.5 2.5-1.12 2.5-2.5-1.12-2.5-2.5-2.5z"/>
                  </svg>
                </div>
                {/* Slack */}
                <div className={`w-10 h-10 rounded-lg flex items-center justify-center ${isDark ? 'bg-dark-700' : 'bg-slate-100'}`}>
                  <svg viewBox="0 0 24 24" className="w-6 h-6">
                    <path fill="#E01E5A" d="M5.042 15.165a2.528 2.528 0 0 1-2.52 2.523A2.528 2.528 0 0 1 0 15.165a2.527 2.527 0 0 1 2.522-2.52h2.52v2.52zM6.313 15.165a2.527 2.527 0 0 1 2.521-2.52 2.527 2.527 0 0 1 2.521 2.52v6.313A2.528 2.528 0 0 1 8.834 24a2.528 2.528 0 0 1-2.521-2.522v-6.313z"/>
                    <path fill="#36C5F0" d="M8.834 5.042a2.528 2.528 0 0 1-2.521-2.52A2.528 2.528 0 0 1 8.834 0a2.528 2.528 0 0 1 2.521 2.522v2.52H8.834zM8.834 6.313a2.528 2.528 0 0 1 2.521 2.521 2.528 2.528 0 0 1-2.521 2.521H2.522A2.528 2.528 0 0 1 0 8.834a2.528 2.528 0 0 1 2.522-2.521h6.312z"/>
                    <path fill="#2EB67D" d="M18.956 8.834a2.528 2.528 0 0 1 2.522-2.521A2.528 2.528 0 0 1 24 8.834a2.528 2.528 0 0 1-2.522 2.521h-2.522V8.834zM17.688 8.834a2.528 2.528 0 0 1-2.523 2.521 2.527 2.527 0 0 1-2.52-2.521V2.522A2.527 2.527 0 0 1 15.165 0a2.528 2.528 0 0 1 2.523 2.522v6.312z"/>
                    <path fill="#ECB22E" d="M15.165 18.956a2.528 2.528 0 0 1 2.523 2.522A2.528 2.528 0 0 1 15.165 24a2.527 2.527 0 0 1-2.52-2.522v-2.522h2.52zM15.165 17.688a2.527 2.527 0 0 1-2.52-2.523 2.526 2.526 0 0 1 2.52-2.52h6.313A2.527 2.527 0 0 1 24 15.165a2.528 2.528 0 0 1-2.522 2.523h-6.313z"/>
                  </svg>
                </div>
              </div>
              <h3 className={`font-semibold mb-2 text-sm sm:text-base ${isDark ? 'text-white' : 'text-slate-900'}`}>
                Connect apps to automate your work
              </h3>
              <p className={`text-xs sm:text-sm mb-4 ${isDark ? 'text-text-secondary' : 'text-slate-500'}`}>
                Integrate apps with SweetPilot so your AI helpers can access, understand, and update your data.
              </p>
              <button
                onClick={() => setShowIntegrationsModal(true)}
                className="btn-primary px-6 w-full sm:w-auto"
              >
                Browse all
              </button>
            </div>

            {/* Right side - Available integrations */}
            <div className="p-3 sm:p-4 space-y-2 sm:space-y-3">
              {availableProviders.slice(0, 4).map((provider) => (
                <div
                  key={provider.id}
                  className={`flex items-center gap-2 sm:gap-3 p-2 sm:p-3 rounded-xl ${isDark ? 'bg-dark-700' : 'bg-slate-50'}`}
                >
                  <div className="flex-shrink-0">
                    <ProviderIcon providerId={provider.id} providerName={provider.name} color={provider.color} />
                  </div>
                  <div className="flex-1 min-w-0">
                    <p className={`font-medium text-sm sm:text-base ${isDark ? 'text-white' : 'text-slate-900'}`}>{provider.name}</p>
                    <p className={`text-xs sm:text-sm truncate ${isDark ? 'text-text-muted' : 'text-slate-500'}`}>
                      {provider.description}
                    </p>
                  </div>
                  <button
                    onClick={() => connectProvider(provider)}
                    disabled={connecting}
                    className="btn-primary px-3 sm:px-4 py-1.5 sm:py-2 text-xs sm:text-sm flex-shrink-0"
                  >
                    Connect
                  </button>
                </div>
              ))}

              {/* Connected integrations */}
              {integrations.slice(0, 2).map((integration) => (
                <div
                  key={integration.id}
                  className={`flex items-center gap-2 sm:gap-3 p-2 sm:p-3 rounded-xl ${isDark ? 'bg-dark-700' : 'bg-slate-50'}`}
                >
                  <div className="flex-shrink-0">
                    <ProviderIcon providerId={integration.provider} providerName={integration.provider_name || integration.provider} color="#22c55e" connected />
                  </div>
                  <div className="flex-1 min-w-0">
                    <p className={`font-medium text-sm sm:text-base ${isDark ? 'text-white' : 'text-slate-900'}`}>
                      {integration.provider_name || integration.provider}
                    </p>
                    <p className={`text-xs sm:text-sm ${isDark ? 'text-text-muted' : 'text-slate-500'}`}>Connected</p>
                  </div>
                  <span className="badge bg-accent-green/20 text-accent-green text-xs px-2 sm:px-3 py-1 flex-shrink-0">
                    Active
                  </span>
                </div>
              ))}
            </div>
          </div>
        </div>
      </motion.div>
      )}

      {/* Knowledge Section - Only show for 'all' or 'memories' filter */}
      {knowledge.length > 0 && (activeFilter === 'all' || activeFilter === 'memories') && (
        <motion.div variants={fadeInUp}>
          <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-3 sm:gap-4">
            {knowledge.map((entry) => (
              <div
                key={entry.id}
                onClick={() => handleViewKnowledge(entry)}
                className={`group relative rounded-2xl border transition-all hover:shadow-lg cursor-pointer overflow-hidden ${isDark ? 'bg-dark-800 border-border hover:border-accent-purple/30' : 'bg-white border-slate-200 hover:border-purple-300 shadow-sm'}`}
              >
                {/* Card Content */}
                <div className="p-4 sm:p-5 min-h-[120px] sm:min-h-[140px] flex flex-col">
                  {/* Category Icon */}
                  <div className={`w-10 h-10 rounded-xl flex items-center justify-center mb-3 ${
                    entry.category === 'brand' ? 'bg-gradient-to-br from-violet-500 to-purple-600' :
                    entry.category === 'business' ? 'bg-blue-500' :
                    entry.category === 'audience' ? 'bg-purple-500' :
                    entry.category === 'marketing' ? 'bg-orange-500' :
                    entry.category === 'social' ? 'bg-pink-500' :
                    'bg-gray-500'
                  }`}>
                    {entry.category === 'brand' && (
                      <svg className="w-5 h-5 text-white" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth="2">
                        <path strokeLinecap="round" strokeLinejoin="round" d="M9.663 17h4.673M12 3v1m6.364 1.636l-.707.707M21 12h-1M4 12H3m3.343-5.657l-.707-.707m2.828 9.9a5 5 0 117.072 0l-.548.547A3.374 3.374 0 0014 18.469V19a2 2 0 11-4 0v-.531c0-.895-.356-1.754-.988-2.386l-.548-.547z" />
                      </svg>
                    )}
                    {entry.category === 'business' && (
                      <svg className="w-5 h-5 text-white" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth="2">
                        <path strokeLinecap="round" strokeLinejoin="round" d="M19 21V5a2 2 0 00-2-2H7a2 2 0 00-2 2v16m14 0h2m-2 0h-5m-9 0H3m2 0h5M9 7h1m-1 4h1m4-4h1m-1 4h1m-5 10v-5a1 1 0 011-1h2a1 1 0 011 1v5m-4 0h4" />
                      </svg>
                    )}
                    {entry.category === 'audience' && (
                      <svg className="w-5 h-5 text-white" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth="2">
                        <path strokeLinecap="round" strokeLinejoin="round" d="M17 20h5v-2a3 3 0 00-5.356-1.857M17 20H7m10 0v-2c0-.656-.126-1.283-.356-1.857M7 20H2v-2a3 3 0 015.356-1.857M7 20v-2c0-.656.126-1.283.356-1.857m0 0a5.002 5.002 0 019.288 0M15 7a3 3 0 11-6 0 3 3 0 016 0zm6 3a2 2 0 11-4 0 2 2 0 014 0zM7 10a2 2 0 11-4 0 2 2 0 014 0z" />
                      </svg>
                    )}
                    {entry.category === 'marketing' && (
                      <svg className="w-5 h-5 text-white" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth="2">
                        <path strokeLinecap="round" strokeLinejoin="round" d="M11 5.882V19.24a1.76 1.76 0 01-3.417.592l-2.147-6.15M18 13a3 3 0 100-6M5.436 13.683A4.001 4.001 0 017 6h1.832c4.1 0 7.625-1.234 9.168-3v14c-1.543-1.766-5.067-3-9.168-3H7a3.988 3.988 0 01-1.564-.317z" />
                      </svg>
                    )}
                    {entry.category === 'social' && (
                      <svg className="w-5 h-5 text-white" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth="2">
                        <path strokeLinecap="round" strokeLinejoin="round" d="M8.684 13.342C8.886 12.938 9 12.482 9 12c0-.482-.114-.938-.316-1.342m0 2.684a3 3 0 110-2.684m0 2.684l6.632 3.316m-6.632-6l6.632-3.316m0 0a3 3 0 105.367-2.684 3 3 0 00-5.367 2.684zm0 9.316a3 3 0 105.368 2.684 3 3 0 00-5.368-2.684z" />
                      </svg>
                    )}
                    {!['brand', 'business', 'audience', 'marketing', 'social'].includes(entry.category) && (
                      <Lightbulb className="w-5 h-5 text-white" />
                    )}
                  </div>

                  {/* Value - Main content */}
                  {entry.key === 'brand_logo' && entry.value ? (
                    /* Show image for brand logo */
                    <div className="flex-1 flex items-center justify-center">
                      <img
                        src={entry.value}
                        alt="Brand Logo"
                        className="max-h-16 max-w-full object-contain"
                        onError={(e) => {
                          (e.target as HTMLImageElement).style.display = 'none';
                          (e.target as HTMLImageElement).nextElementSibling?.classList.remove('hidden');
                        }}
                      />
                      <span className={`hidden text-sm ${isDark ? 'text-text-secondary' : 'text-slate-600'}`}>
                        Logo image
                      </span>
                    </div>
                  ) : entry.key === 'brand_colors' && entry.value ? (
                    /* Show color swatches for brand colors */
                    <div className="flex-1 flex items-center gap-2">
                      {(() => {
                        try {
                          const colors = JSON.parse(entry.value);
                          const colorArray = [colors.first, colors.second, colors.third, colors.fourth, colors.fifth].filter(Boolean);
                          return colorArray.length > 0 ? (
                            colorArray.map((color: string, idx: number) => (
                              <div
                                key={idx}
                                className="w-8 h-8 rounded-full shadow-sm border border-white/20"
                                style={{ backgroundColor: color }}
                                title={color}
                              />
                            ))
                          ) : (
                            <span className={`text-sm ${isDark ? 'text-text-secondary' : 'text-slate-600'}`}>No colors</span>
                          );
                        } catch {
                          return <span className={`text-sm ${isDark ? 'text-text-secondary' : 'text-slate-600'}`}>{entry.value}</span>;
                        }
                      })()}
                    </div>
                  ) : (
                    /* Default text display */
                    <p className={`text-sm line-clamp-2 flex-1 ${isDark ? 'text-text-secondary' : 'text-slate-600'}`}>
                      {entry.value || 'none'}
                    </p>
                  )}
                </div>

                {/* Label Footer */}
                <div className={`px-4 sm:px-5 py-2 sm:py-3 border-t ${isDark ? 'border-border bg-dark-900/50' : 'border-slate-100 bg-slate-50/50'}`}>
                  <p className={`text-xs font-medium truncate ${isDark ? 'text-text-muted' : 'text-slate-500'}`}>
                    {entry.label}
                  </p>
                </div>
              </div>
            ))}
          </div>
        </motion.div>
      )}

      {/* Documents Section */}
      {filteredDocs.length > 0 && (
        <motion.div variants={fadeInUp}>
          <div className="flex items-center justify-between mb-4">
            <h2 className={`text-lg font-semibold ${isDark ? 'text-white' : 'text-slate-900'}`}>
              {activeFilter === 'all' ? 'All Content' : activeFilter.charAt(0).toUpperCase() + activeFilter.slice(1)}
              <span className={`ml-2 text-sm font-normal ${isDark ? 'text-text-muted' : 'text-slate-400'}`}>
                ({filteredDocs.length})
              </span>
            </h2>
            {/* Add button for filtered content types */}
            {(activeFilter === 'images' || activeFilter === 'videos' || activeFilter === 'documents') && (
              <button
                onClick={() => setShowAddContent(true)}
                className={`flex items-center gap-2 px-4 py-2 rounded-xl text-sm font-medium transition-colors ${
                  isDark
                    ? 'bg-dark-700 text-text-secondary hover:text-white hover:bg-dark-600'
                    : 'bg-slate-100 text-slate-600 hover:text-slate-900 hover:bg-slate-200'
                }`}
              >
                <Plus className="w-4 h-4" />
                Add {activeFilter === 'images' ? 'Images' : activeFilter === 'videos' ? 'Videos' : 'Documents'}
              </button>
            )}
          </div>
          <div className="space-y-3">
            {filteredDocs.map((doc) => {
              const DocIcon = getDocIcon(doc.type);
              return (
                <div
                  key={doc.id}
                  className={`group p-4 rounded-xl border transition-all ${isDark ? 'bg-dark-800 border-border hover:border-border-light' : 'bg-white border-slate-200 hover:border-slate-300 shadow-sm'}`}
                >
                  <div className="flex items-start gap-4">
                    <div className={`w-10 h-10 rounded-xl flex items-center justify-center flex-shrink-0 ${isDark ? 'bg-dark-700' : 'bg-slate-100'}`}>
                      <DocIcon className={`w-5 h-5 ${doc.type === 'url' ? 'text-accent-cyan' : 'text-accent-purple'}`} />
                    </div>
                    <div className="flex-1 min-w-0">
                      <div className="flex items-start justify-between gap-2">
                        <div className="min-w-0">
                          <p className={`font-medium truncate ${isDark ? 'text-white' : 'text-slate-900'}`}>{doc.name}</p>
                          <p className={`text-xs mt-1 ${isDark ? 'text-text-muted' : 'text-slate-500'}`}>
                            {doc.chunks_count} chunks • {formatDate(doc.created_at)}
                          </p>
                        </div>
                        <span className={`badge text-xs flex-shrink-0 ${
                          doc.status === 'ready' ? 'bg-accent-green/20 text-accent-green' :
                          doc.status === 'processing' ? 'bg-accent-orange/20 text-accent-orange' :
                          'bg-accent-red/20 text-accent-red'
                        }`}>
                          {doc.status === 'processing' && <RefreshCw className="w-3 h-3 mr-1 animate-spin" />}
                          {doc.status}
                        </span>
                      </div>
                      {doc.content_summary && (
                        <p className={`text-sm mt-2 line-clamp-2 ${isDark ? 'text-text-secondary' : 'text-slate-600'}`}>
                          {doc.content_summary}
                        </p>
                      )}
                      {doc.source_url && (
                        <a
                          href={doc.source_url}
                          target="_blank"
                          rel="noopener noreferrer"
                          className={`text-xs mt-2 flex items-center gap-1 hover:underline ${isDark ? 'text-accent-cyan' : 'text-blue-500'}`}
                        >
                          <ExternalLink className="w-3 h-3" />
                          {doc.source_url}
                        </a>
                      )}
                    </div>
                    <button
                      onClick={() => handleDeleteDocument(doc.id)}
                      className={`p-2 rounded-lg opacity-0 group-hover:opacity-100 transition-opacity ${isDark ? 'hover:bg-dark-700' : 'hover:bg-slate-100'}`}
                    >
                      <Trash2 className="w-4 h-4 text-accent-red" />
                    </button>
                  </div>
                </div>
              );
            })}
          </div>
        </motion.div>
      )}

      {/* Filter-specific Empty States */}
      {activeFilter === 'images' && filteredDocs.length === 0 && (
        <motion.div variants={fadeInUp} className={`text-center py-16 rounded-2xl border ${isDark ? 'bg-dark-800 border-border' : 'bg-white border-slate-200 shadow-sm'}`}>
          {/* Image icon - orange/yellow gradient style */}
          <div className="w-20 h-20 mx-auto mb-6 rounded-2xl bg-gradient-to-br from-amber-400 to-orange-500 flex items-center justify-center shadow-lg">
            <svg className="w-10 h-10 text-white" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
              <rect x="3" y="3" width="18" height="18" rx="2" ry="2"/>
              <circle cx="8.5" cy="8.5" r="1.5"/>
              <polyline points="21 15 16 10 5 21"/>
            </svg>
          </div>
          <h3 className={`text-xl font-semibold mb-3 ${isDark ? 'text-white' : 'text-slate-900'}`}>
            Upload your first images
          </h3>
          <p className={`text-sm mb-6 max-w-md mx-auto ${isDark ? 'text-text-secondary' : 'text-slate-500'}`}>
            Add images to Brain AI so it can better understand your products, brand, and visual style. Use images when creating posts for Instagram, Facebook, or LinkedIn.
          </p>
          <button
            onClick={() => setShowAddContent(true)}
            className="btn-primary inline-flex items-center gap-2"
          >
            <Plus className="w-4 h-4" />
            Add images
          </button>
        </motion.div>
      )}

      {activeFilter === 'videos' && filteredDocs.length === 0 && (
        <motion.div variants={fadeInUp} className={`text-center py-16 rounded-2xl border ${isDark ? 'bg-dark-800 border-border' : 'bg-white border-slate-200 shadow-sm'}`}>
          {/* Video icon - teal/green gradient style */}
          <div className="w-20 h-20 mx-auto mb-6 rounded-2xl bg-gradient-to-br from-teal-400 to-emerald-600 flex items-center justify-center shadow-lg">
            <svg className="w-10 h-10 text-white" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
              <rect x="2" y="4" width="20" height="16" rx="2"/>
              <path d="M2 8h20"/>
              <path d="M2 16h20"/>
              <circle cx="12" cy="12" r="2"/>
            </svg>
          </div>
          <h3 className={`text-xl font-semibold mb-3 ${isDark ? 'text-white' : 'text-slate-900'}`}>
            Upload your first videos
          </h3>
          <p className={`text-sm mb-6 max-w-md mx-auto ${isDark ? 'text-text-secondary' : 'text-slate-500'}`}>
            Add videos to Brain AI so it can learn from your content and let you easily use clips when creating posts for Instagram, Facebook, or LinkedIn.
          </p>
          <button
            onClick={() => setShowAddContent(true)}
            className="btn-primary inline-flex items-center gap-2"
          >
            <Plus className="w-4 h-4" />
            Add videos
          </button>
        </motion.div>
      )}

      {activeFilter === 'webpages' && filteredDocs.length === 0 && (
        <motion.div variants={fadeInUp} className={`text-center py-16 rounded-2xl border ${isDark ? 'bg-dark-800 border-border' : 'bg-white border-slate-200 shadow-sm'}`}>
          {/* Webpage icon - purple gradient style */}
          <div className="w-20 h-20 mx-auto mb-6 rounded-2xl bg-gradient-to-br from-purple-500 to-violet-700 flex items-center justify-center shadow-lg">
            <svg className="w-10 h-10 text-white" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
              <rect x="3" y="3" width="18" height="18" rx="2"/>
              <circle cx="12" cy="12" r="5"/>
              <path d="M12 7v10"/>
              <path d="M7 12h10"/>
            </svg>
          </div>
          <h3 className={`text-xl font-semibold mb-3 ${isDark ? 'text-white' : 'text-slate-900'}`}>
            Add your first webpages
          </h3>
          <p className={`text-sm mb-6 max-w-md mx-auto ${isDark ? 'text-text-secondary' : 'text-slate-500'}`}>
            Share links to your website so Brain AI can learn your messaging, images, and key information—helping it generate smarter, more consistent, on-brand content.
          </p>
          <button
            onClick={() => setShowAnalyzeWebsiteModal(true)}
            className="btn-primary inline-flex items-center gap-2"
          >
            <Plus className="w-4 h-4" />
            Add webpages
          </button>
        </motion.div>
      )}

      {/* Webpages Section Header with Add Button - Show when webpages filter is active and has content */}
      {activeFilter === 'webpages' && filteredDocs.length > 0 && (
        <motion.div variants={fadeInUp} className="flex items-center justify-between">
          <h2 className={`text-lg font-semibold ${isDark ? 'text-white' : 'text-slate-900'}`}>
            Webpages
            <span className={`ml-2 text-sm font-normal ${isDark ? 'text-text-muted' : 'text-slate-400'}`}>
              ({filteredDocs.length})
            </span>
          </h2>
          <button
            onClick={() => setShowAnalyzeWebsiteModal(true)}
            className="btn-primary inline-flex items-center gap-2 text-sm"
          >
            <Plus className="w-4 h-4" />
            Add webpages
          </button>
        </motion.div>
      )}

      {activeFilter === 'documents' && filteredDocs.length === 0 && (
        <motion.div variants={fadeInUp} className={`text-center py-16 rounded-2xl border ${isDark ? 'bg-dark-800 border-border' : 'bg-white border-slate-200 shadow-sm'}`}>
          {/* Document icon - orange gradient style */}
          <div className="w-20 h-20 mx-auto mb-6 rounded-2xl bg-gradient-to-br from-orange-400 to-amber-600 flex items-center justify-center shadow-lg">
            <svg className="w-10 h-10 text-white" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
              <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z"/>
              <path d="M14 2v6h6"/>
              <line x1="8" y1="13" x2="16" y2="13"/>
              <line x1="8" y1="17" x2="16" y2="17"/>
            </svg>
          </div>
          <h3 className={`text-xl font-semibold mb-3 ${isDark ? 'text-white' : 'text-slate-900'}`}>
            Add your first documents
          </h3>
          <p className={`text-sm mb-6 max-w-md mx-auto ${isDark ? 'text-text-secondary' : 'text-slate-500'}`}>
            Add PDFs, decks, and text files to help Brain AI understand your company's knowledge. It can summarize, search, and generate with your content in mind.
          </p>
          <button
            onClick={() => setShowAddContent(true)}
            className="btn-primary inline-flex items-center gap-2"
          >
            <Plus className="w-4 h-4" />
            Add document
          </button>
        </motion.div>
      )}

      {/* General Empty State - for 'all' or 'memories' filter when no content exists */}
      {(activeFilter === 'all' || activeFilter === 'memories') && filteredDocs.length === 0 && knowledge.length === 0 && (
        <motion.div variants={fadeInUp} className={`text-center py-16 rounded-2xl border ${isDark ? 'bg-dark-800 border-border' : 'bg-white border-slate-200 shadow-sm'}`}>
          <Brain className={`w-16 h-16 mx-auto mb-4 ${isDark ? 'text-text-muted' : 'text-slate-300'}`} />
          <h3 className={`text-lg font-semibold mb-2 ${isDark ? 'text-white' : 'text-slate-900'}`}>
            {searchQuery ? 'No results found' : 'Your brain is empty'}
          </h3>
          <p className={`text-sm mb-6 ${isDark ? 'text-text-muted' : 'text-slate-500'}`}>
            {searchQuery ? 'Try a different search term' : 'Add documents, URLs, or answer questions to train your AI'}
          </p>
          <button
            onClick={() => setShowAddContent(true)}
            className="btn-primary inline-flex items-center gap-2"
          >
            <Plus className="w-4 h-4" />
            Add Knowledge
          </button>
        </motion.div>
      )}

      {/* Add Content Modal */}
      <AnimatePresence>
        {showAddContent && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4"
            onClick={() => setShowAddContent(false)}
          >
            <motion.div
              initial={{ scale: 0.95, opacity: 0 }}
              animate={{ scale: 1, opacity: 1 }}
              exit={{ scale: 0.95, opacity: 0 }}
              onClick={(e) => e.stopPropagation()}
              className={`w-full max-w-lg rounded-2xl overflow-hidden ${isDark ? 'bg-dark-800 border border-border' : 'bg-white'}`}
            >
              <div className={`px-6 py-4 border-b flex items-center justify-between ${isDark ? 'border-border' : 'border-slate-200'}`}>
                <h3 className={`text-lg font-semibold ${isDark ? 'text-white' : 'text-slate-900'}`}>
                  {activeFilter === 'images' ? 'Add Images' :
                   activeFilter === 'videos' ? 'Add Videos' :
                   activeFilter === 'webpages' ? 'Add Website' :
                   activeFilter === 'documents' ? 'Add Documents' : 'Add Knowledge'}
                </h3>
                <button onClick={() => setShowAddContent(false)} className={isDark ? 'text-text-muted hover:text-white' : 'text-slate-400 hover:text-slate-600'}>
                  <X className="w-5 h-5" />
                </button>
              </div>
              <div className="p-6 space-y-6">
                {/* Upload Files - Show for all except webpages */}
                {showFileUpload && (
                  <div>
                    <label className={`block text-sm font-medium mb-2 ${isDark ? 'text-white' : 'text-slate-700'}`}>{getUploadLabel().title}</label>
                    <div
                      onClick={() => fileInputRef.current?.click()}
                      onDragOver={(e) => { e.preventDefault(); e.stopPropagation(); }}
                      onDrop={(e) => { e.preventDefault(); e.stopPropagation(); handleFileUpload(e.dataTransfer.files); }}
                      className={`p-8 rounded-xl border-2 border-dashed text-center cursor-pointer transition-colors ${isDark ? 'bg-dark-700 border-border hover:border-accent-purple/50' : 'bg-slate-50 border-slate-200 hover:border-purple-300'}`}
                    >
                      <input
                        ref={fileInputRef}
                        type="file"
                        className="hidden"
                        accept={getAcceptedFileTypes()}
                        multiple
                        onChange={(e) => handleFileUpload(e.target.files)}
                      />
                      <div className="w-12 h-12 rounded-xl bg-accent-purple/10 flex items-center justify-center mx-auto mb-3">
                        {isUploading ? (
                          <RefreshCw className="w-6 h-6 text-accent-purple animate-spin" />
                        ) : activeFilter === 'images' ? (
                          <Image className="w-6 h-6 text-accent-purple" />
                        ) : activeFilter === 'videos' ? (
                          <Video className="w-6 h-6 text-accent-purple" />
                        ) : activeFilter === 'documents' ? (
                          <FileText className="w-6 h-6 text-accent-purple" />
                        ) : (
                          <Upload className="w-6 h-6 text-accent-purple" />
                        )}
                      </div>
                      <p className={`text-sm font-medium mb-1 ${isDark ? 'text-white' : 'text-slate-900'}`}>
                        {isUploading ? 'Uploading...' : 'Click or drag files here'}
                      </p>
                      <p className={`text-xs ${isDark ? 'text-text-muted' : 'text-slate-500'}`}>
                        {getUploadLabel().hint}
                      </p>
                    </div>
                  </div>
                )}

                {/* Add URL - Show only for all, memories, and webpages */}
                {showUrlInput && (
                  <div>
                    <label className={`block text-sm font-medium mb-2 ${isDark ? 'text-white' : 'text-slate-700'}`}>Add Website URL</label>
                    <div className="flex gap-2">
                      <div className="relative flex-1">
                        <Globe className={`absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 ${isDark ? 'text-text-muted' : 'text-slate-400'}`} />
                        <input
                          type="url"
                          value={urlInput}
                          onChange={(e) => setUrlInput(e.target.value)}
                          placeholder="https://example.com"
                          className={`w-full pl-10 pr-4 py-2.5 rounded-xl border text-sm focus:outline-none focus:border-accent-purple focus:ring-2 focus:ring-accent-purple/20 ${isDark ? 'bg-dark-700 border-border text-white placeholder-text-muted' : 'bg-white border-slate-200 text-slate-900 placeholder-slate-400'}`}
                          onKeyDown={(e) => { if (e.key === 'Enter') handleAddUrl(); }}
                        />
                      </div>
                      <button
                        onClick={handleAddUrl}
                        disabled={!urlInput.trim() || isUploading}
                        className="btn-primary px-4 disabled:opacity-50"
                      >
                        <Plus className="w-4 h-4" />
                      </button>
                    </div>
                  </div>
                )}

                {/* Quick Actions - Only show for all/memories filter */}
                {(activeFilter === 'all' || activeFilter === 'memories') && (
                <div>
                  <label className={`block text-sm font-medium mb-2 ${isDark ? 'text-white' : 'text-slate-700'}`}>Quick Actions</label>
                  <div className="grid grid-cols-2 gap-2">
                    {[
                      { icon: MessageSquare, label: 'Add from Chat', desc: 'Save chat responses' },
                      { icon: Zap, label: 'Import from Integration', desc: 'Sync connected apps' },
                    ].map((action) => (
                      <button
                        key={action.label}
                        className={`p-3 rounded-xl text-left transition-colors ${isDark ? 'bg-dark-700 hover:bg-dark-600' : 'bg-slate-50 hover:bg-slate-100'}`}
                      >
                        <action.icon className={`w-5 h-5 mb-2 ${isDark ? 'text-accent-purple' : 'text-purple-500'}`} />
                        <p className={`text-sm font-medium ${isDark ? 'text-white' : 'text-slate-900'}`}>{action.label}</p>
                        <p className={`text-xs ${isDark ? 'text-text-muted' : 'text-slate-500'}`}>{action.desc}</p>
                      </button>
                    ))}
                  </div>
                </div>
                )}
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>

      {/* Questions Modal - One question at a time */}
      <AnimatePresence>
        {showQuestionsModal && currentQuestion && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4"
            onClick={() => setShowQuestionsModal(false)}
          >
            <motion.div
              initial={{ scale: 0.95, opacity: 0 }}
              animate={{ scale: 1, opacity: 1 }}
              exit={{ scale: 0.95, opacity: 0 }}
              onClick={(e) => e.stopPropagation()}
              className={`w-full max-w-lg rounded-2xl overflow-hidden ${isDark ? 'bg-dark-800 border border-border' : 'bg-white'}`}
            >
              {/* Header with progress */}
              <div className={`px-6 py-4 border-b ${isDark ? 'border-border' : 'border-slate-200'}`}>
                <div className="flex items-center justify-between mb-3">
                  <h3 className={`text-lg font-semibold ${isDark ? 'text-white' : 'text-slate-900'}`}>
                    Help your AI get smarter
                  </h3>
                  <button
                    onClick={() => setShowQuestionsModal(false)}
                    className={isDark ? 'text-text-muted hover:text-white' : 'text-slate-400 hover:text-slate-600'}
                  >
                    <X className="w-5 h-5" />
                  </button>
                </div>
                {/* Progress bar */}
                <div className="flex items-center gap-3">
                  <div className={`flex-1 h-2 rounded-full ${isDark ? 'bg-dark-700' : 'bg-slate-200'}`}>
                    <div
                      className="h-2 rounded-full bg-gradient-accent transition-all duration-300"
                      style={{ width: `${((currentQuestionIndex + 1) / totalQuestions) * 100}%` }}
                    />
                  </div>
                  <span className={`text-sm font-medium ${isDark ? 'text-text-secondary' : 'text-slate-500'}`}>
                    {currentQuestionIndex + 1}/{totalQuestions}
                  </span>
                </div>
              </div>

              {/* Question */}
              <div className="p-6">
                <div className="mb-6">
                  <div className="w-12 h-12 rounded-xl bg-gradient-accent flex items-center justify-center mb-4">
                    <Sparkles className="w-6 h-6 text-white" />
                  </div>
                  <h4 className={`text-xl font-semibold mb-2 ${isDark ? 'text-white' : 'text-slate-900'}`}>
                    {currentQuestion.question}
                  </h4>
                  <p className={`text-sm ${isDark ? 'text-text-muted' : 'text-slate-500'}`}>
                    Category: {currentQuestion.category.charAt(0).toUpperCase() + currentQuestion.category.slice(1)}
                  </p>
                </div>

                {/* Answer input */}
                <div className="relative">
                  <textarea
                    value={questionAnswer}
                    onChange={(e) => setQuestionAnswer(e.target.value)}
                    placeholder="Type your answer here..."
                    rows={4}
                    className={`w-full px-4 py-3 rounded-xl border text-sm focus:outline-none focus:border-accent-purple focus:ring-2 focus:ring-accent-purple/20 resize-none ${isDark ? 'bg-dark-700 border-border text-white placeholder-text-muted' : 'bg-slate-50 border-slate-200 text-slate-900 placeholder-slate-400'}`}
                    onKeyDown={(e) => {
                      if (e.key === 'Enter' && !e.shiftKey) {
                        e.preventDefault();
                        handleSaveAnswer();
                      }
                    }}
                  />
                </div>

                {/* Actions */}
                <div className="flex items-center justify-between mt-6">
                  <button
                    onClick={handleSkipQuestion}
                    className={`text-sm font-medium ${isDark ? 'text-text-secondary hover:text-white' : 'text-slate-500 hover:text-slate-900'}`}
                  >
                    Skip this question
                  </button>
                  <button
                    onClick={handleSaveAnswer}
                    disabled={!questionAnswer.trim() || savingAnswer}
                    className="btn-primary flex items-center gap-2 disabled:opacity-50"
                  >
                    {savingAnswer ? (
                      <>
                        <RefreshCw className="w-4 h-4 animate-spin" />
                        Saving...
                      </>
                    ) : (
                      <>
                        <Send className="w-4 h-4" />
                        {currentQuestionIndex < totalQuestions - 1 ? 'Next Question' : 'Finish'}
                      </>
                    )}
                  </button>
                </div>
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>

      {/* Knowledge View/Edit Modal */}
      <AnimatePresence>
        {showKnowledgeModal && selectedKnowledge && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4"
            onClick={() => {
              setShowKnowledgeModal(false);
              setSelectedKnowledge(null);
              setIsEditing(false);
            }}
          >
            <motion.div
              initial={{ scale: 0.95, opacity: 0 }}
              animate={{ scale: 1, opacity: 1 }}
              exit={{ scale: 0.95, opacity: 0 }}
              onClick={(e) => e.stopPropagation()}
              className={`w-full max-w-2xl rounded-2xl overflow-hidden ${isDark ? 'bg-dark-800 border border-border' : 'bg-white'}`}
            >
              {/* Header */}
              <div className={`px-6 py-4 border-b flex items-center justify-between ${isDark ? 'border-border' : 'border-slate-200'}`}>
                <h3 className={`text-lg font-semibold ${isDark ? 'text-white' : 'text-slate-900'}`}>
                  {isEditing ? 'Edit Content' : 'Memory'}
                </h3>
                <button
                  onClick={() => {
                    setShowKnowledgeModal(false);
                    setSelectedKnowledge(null);
                    setIsEditing(false);
                  }}
                  className={isDark ? 'text-text-muted hover:text-white' : 'text-slate-400 hover:text-slate-600'}
                >
                  <X className="w-5 h-5" />
                </button>
              </div>

              {/* Content */}
              <div className="p-6">
                {!isEditing ? (
                  // View Mode
                  <>
                    {/* Title */}
                    <div className="mb-4">
                      <label className={`block text-sm font-medium mb-2 ${isDark ? 'text-text-secondary' : 'text-slate-500'}`}>
                        Title
                      </label>
                      <p className={`text-lg font-semibold ${isDark ? 'text-white' : 'text-slate-900'}`}>
                        {selectedKnowledge.label}
                      </p>
                    </div>

                    {/* Content */}
                    <div className="mb-6">
                      <label className={`block text-sm font-medium mb-2 ${isDark ? 'text-text-secondary' : 'text-slate-500'}`}>
                        Content
                      </label>
                      <div
                        className={`p-4 rounded-xl border min-h-[120px] ${isDark ? 'bg-dark-700 border-border text-white' : 'bg-slate-50 border-slate-200 text-slate-900'}`}
                      >
                        <p className="whitespace-pre-wrap">{selectedKnowledge.value}</p>
                      </div>
                    </div>

                    {/* Personal use toggle */}
                    <div className={`flex items-center justify-between p-4 rounded-xl mb-6 ${isDark ? 'bg-dark-700' : 'bg-slate-50'}`}>
                      <div className="flex items-center gap-3">
                        {personalUseOnly ? (
                          <Lock className={`w-5 h-5 ${isDark ? 'text-accent-purple' : 'text-purple-500'}`} />
                        ) : (
                          <Unlock className={`w-5 h-5 ${isDark ? 'text-text-muted' : 'text-slate-400'}`} />
                        )}
                        <div>
                          <p className={`font-medium ${isDark ? 'text-white' : 'text-slate-900'}`}>Personal use only</p>
                          <p className={`text-sm ${isDark ? 'text-text-muted' : 'text-slate-500'}`}>
                            Only visible to you
                          </p>
                        </div>
                      </div>
                      <button
                        onClick={() => setPersonalUseOnly(!personalUseOnly)}
                        className={`relative w-12 h-6 rounded-full transition-colors ${personalUseOnly ? 'bg-accent-purple' : isDark ? 'bg-dark-600' : 'bg-slate-300'}`}
                      >
                        <span
                          className={`absolute top-1 w-4 h-4 rounded-full bg-white transition-all ${personalUseOnly ? 'left-7' : 'left-1'}`}
                        />
                      </button>
                    </div>

                    {/* Actions */}
                    <div className="flex items-center justify-between">
                      <button
                        onClick={handleDeleteKnowledge}
                        disabled={deletingKnowledge}
                        className={`flex items-center gap-2 px-4 py-2 rounded-xl text-sm font-medium transition-colors ${isDark ? 'text-accent-red hover:bg-accent-red/10' : 'text-red-600 hover:bg-red-50'}`}
                      >
                        {deletingKnowledge ? (
                          <RefreshCw className="w-4 h-4 animate-spin" />
                        ) : (
                          <Trash2 className="w-4 h-4" />
                        )}
                        Delete
                      </button>
                      <div className="flex items-center gap-3">
                        <button
                          onClick={() => setIsEditing(true)}
                          className={`px-4 py-2 rounded-xl text-sm font-medium border ${isDark ? 'border-border text-white hover:bg-dark-700' : 'border-slate-200 text-slate-700 hover:bg-slate-50'}`}
                        >
                          Edit
                        </button>
                        <button
                          onClick={() => {
                            setShowKnowledgeModal(false);
                            setSelectedKnowledge(null);
                          }}
                          className="btn-primary px-6"
                        >
                          Done
                        </button>
                      </div>
                    </div>
                  </>
                ) : (
                  // Edit Mode
                  <>
                    {/* Title (read-only in edit mode) */}
                    <div className="mb-4">
                      <label className={`block text-sm font-medium mb-2 ${isDark ? 'text-text-secondary' : 'text-slate-500'}`}>
                        Title
                      </label>
                      <p className={`text-lg font-semibold ${isDark ? 'text-white' : 'text-slate-900'}`}>
                        {selectedKnowledge.label}
                      </p>
                    </div>

                    {/* Rich Text Toolbar */}
                    <div className={`flex items-center gap-1 p-2 rounded-t-xl border border-b-0 ${isDark ? 'bg-dark-700 border-border' : 'bg-slate-100 border-slate-200'}`}>
                      <button
                        onClick={() => applyFormatting('bold')}
                        className={`p-2 rounded-lg transition-colors ${isDark ? 'hover:bg-dark-600 text-text-secondary hover:text-white' : 'hover:bg-slate-200 text-slate-600 hover:text-slate-900'}`}
                        title="Bold"
                      >
                        <Bold className="w-4 h-4" />
                      </button>
                      <button
                        onClick={() => applyFormatting('italic')}
                        className={`p-2 rounded-lg transition-colors ${isDark ? 'hover:bg-dark-600 text-text-secondary hover:text-white' : 'hover:bg-slate-200 text-slate-600 hover:text-slate-900'}`}
                        title="Italic"
                      >
                        <Italic className="w-4 h-4" />
                      </button>
                      <button
                        onClick={() => applyFormatting('strikethrough')}
                        className={`p-2 rounded-lg transition-colors ${isDark ? 'hover:bg-dark-600 text-text-secondary hover:text-white' : 'hover:bg-slate-200 text-slate-600 hover:text-slate-900'}`}
                        title="Strikethrough"
                      >
                        <Strikethrough className="w-4 h-4" />
                      </button>

                      <div className={`w-px h-6 mx-1 ${isDark ? 'bg-border' : 'bg-slate-300'}`} />

                      <button
                        onClick={() => applyFormatting('h1')}
                        className={`p-2 rounded-lg transition-colors ${isDark ? 'hover:bg-dark-600 text-text-secondary hover:text-white' : 'hover:bg-slate-200 text-slate-600 hover:text-slate-900'}`}
                        title="Heading 1"
                      >
                        <Heading1 className="w-4 h-4" />
                      </button>
                      <button
                        onClick={() => applyFormatting('h2')}
                        className={`p-2 rounded-lg transition-colors ${isDark ? 'hover:bg-dark-600 text-text-secondary hover:text-white' : 'hover:bg-slate-200 text-slate-600 hover:text-slate-900'}`}
                        title="Heading 2"
                      >
                        <Heading2 className="w-4 h-4" />
                      </button>
                      <button
                        onClick={() => applyFormatting('h3')}
                        className={`p-2 rounded-lg transition-colors ${isDark ? 'hover:bg-dark-600 text-text-secondary hover:text-white' : 'hover:bg-slate-200 text-slate-600 hover:text-slate-900'}`}
                        title="Heading 3"
                      >
                        <Heading3 className="w-4 h-4" />
                      </button>

                      <div className={`w-px h-6 mx-1 ${isDark ? 'bg-border' : 'bg-slate-300'}`} />

                      <button
                        onClick={() => applyFormatting('bullet')}
                        className={`p-2 rounded-lg transition-colors ${isDark ? 'hover:bg-dark-600 text-text-secondary hover:text-white' : 'hover:bg-slate-200 text-slate-600 hover:text-slate-900'}`}
                        title="Bullet List"
                      >
                        <List className="w-4 h-4" />
                      </button>
                      <button
                        onClick={() => applyFormatting('numbered')}
                        className={`p-2 rounded-lg transition-colors ${isDark ? 'hover:bg-dark-600 text-text-secondary hover:text-white' : 'hover:bg-slate-200 text-slate-600 hover:text-slate-900'}`}
                        title="Numbered List"
                      >
                        <ListOrdered className="w-4 h-4" />
                      </button>
                      <button
                        onClick={() => applyFormatting('quote')}
                        className={`p-2 rounded-lg transition-colors ${isDark ? 'hover:bg-dark-600 text-text-secondary hover:text-white' : 'hover:bg-slate-200 text-slate-600 hover:text-slate-900'}`}
                        title="Quote"
                      >
                        <Quote className="w-4 h-4" />
                      </button>
                      <button
                        onClick={() => applyFormatting('hr')}
                        className={`p-2 rounded-lg transition-colors ${isDark ? 'hover:bg-dark-600 text-text-secondary hover:text-white' : 'hover:bg-slate-200 text-slate-600 hover:text-slate-900'}`}
                        title="Horizontal Line"
                      >
                        <Minus className="w-4 h-4" />
                      </button>
                    </div>

                    {/* Content Editor */}
                    <div className="mb-6">
                      <textarea
                        ref={contentEditRef as any}
                        value={editedContent}
                        onChange={(e) => setEditedContent(e.target.value)}
                        className={`w-full px-4 py-3 rounded-b-xl border text-sm focus:outline-none focus:border-accent-purple focus:ring-2 focus:ring-accent-purple/20 min-h-[200px] resize-none ${isDark ? 'bg-dark-700 border-border text-white placeholder-text-muted' : 'bg-white border-slate-200 text-slate-900 placeholder-slate-400'}`}
                        placeholder="Enter your content here..."
                      />
                    </div>

                    {/* Personal use toggle */}
                    <div className={`flex items-center justify-between p-4 rounded-xl mb-6 ${isDark ? 'bg-dark-700' : 'bg-slate-50'}`}>
                      <div className="flex items-center gap-3">
                        {personalUseOnly ? (
                          <Lock className={`w-5 h-5 ${isDark ? 'text-accent-purple' : 'text-purple-500'}`} />
                        ) : (
                          <Unlock className={`w-5 h-5 ${isDark ? 'text-text-muted' : 'text-slate-400'}`} />
                        )}
                        <div>
                          <p className={`font-medium ${isDark ? 'text-white' : 'text-slate-900'}`}>Personal use only</p>
                          <p className={`text-sm ${isDark ? 'text-text-muted' : 'text-slate-500'}`}>
                            Only visible to you
                          </p>
                        </div>
                      </div>
                      <button
                        onClick={() => setPersonalUseOnly(!personalUseOnly)}
                        className={`relative w-12 h-6 rounded-full transition-colors ${personalUseOnly ? 'bg-accent-purple' : isDark ? 'bg-dark-600' : 'bg-slate-300'}`}
                      >
                        <span
                          className={`absolute top-1 w-4 h-4 rounded-full bg-white transition-all ${personalUseOnly ? 'left-7' : 'left-1'}`}
                        />
                      </button>
                    </div>

                    {/* Actions */}
                    <div className="flex items-center justify-between">
                      <button
                        onClick={handleDeleteKnowledge}
                        disabled={deletingKnowledge}
                        className={`flex items-center gap-2 px-4 py-2 rounded-xl text-sm font-medium transition-colors ${isDark ? 'text-accent-red hover:bg-accent-red/10' : 'text-red-600 hover:bg-red-50'}`}
                      >
                        {deletingKnowledge ? (
                          <RefreshCw className="w-4 h-4 animate-spin" />
                        ) : (
                          <Trash2 className="w-4 h-4" />
                        )}
                        Delete
                      </button>
                      <div className="flex items-center gap-3">
                        <button
                          onClick={() => {
                            setIsEditing(false);
                            setEditedContent(selectedKnowledge.value);
                          }}
                          className={`px-4 py-2 rounded-xl text-sm font-medium border ${isDark ? 'border-border text-white hover:bg-dark-700' : 'border-slate-200 text-slate-700 hover:bg-slate-50'}`}
                        >
                          Cancel
                        </button>
                        <button
                          onClick={handleSaveKnowledge}
                          disabled={savingKnowledge || editedContent === selectedKnowledge.value}
                          className="btn-primary px-6 flex items-center gap-2 disabled:opacity-50"
                        >
                          {savingKnowledge ? (
                            <>
                              <RefreshCw className="w-4 h-4 animate-spin" />
                              Saving...
                            </>
                          ) : (
                            'Save'
                          )}
                        </button>
                      </div>
                    </div>
                  </>
                )}
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>

      {/* Add Webpages Modal */}
      <AnimatePresence>
        {showAnalyzeWebsiteModal && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4"
            onClick={() => {
              setShowAnalyzeWebsiteModal(false);
              setPendingUrls([]);
              setUrlInputForList('');
              setAnalyzeAsBrand(false);
            }}
          >
            <motion.div
              initial={{ scale: 0.95, opacity: 0 }}
              animate={{ scale: 1, opacity: 1 }}
              exit={{ scale: 0.95, opacity: 0 }}
              onClick={(e) => e.stopPropagation()}
              className={`w-full max-w-xl rounded-2xl overflow-hidden ${isDark ? 'bg-dark-800 border border-border' : 'bg-white'}`}
            >
              {/* Header */}
              <div className={`px-6 py-4 border-b flex items-center justify-between ${isDark ? 'border-border' : 'border-slate-200'}`}>
                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 rounded-xl bg-gradient-to-br from-violet-500 to-purple-600 flex items-center justify-center">
                    <Globe className="w-5 h-5 text-white" />
                  </div>
                  <h3 className={`text-lg font-semibold ${isDark ? 'text-white' : 'text-slate-900'}`}>
                    Add Webpages
                  </h3>
                </div>
                <button
                  onClick={() => {
                    setShowAnalyzeWebsiteModal(false);
                    setPendingUrls([]);
                    setUrlInputForList('');
                    setAnalyzeAsBrand(false);
                  }}
                  className={isDark ? 'text-text-muted hover:text-white' : 'text-slate-400 hover:text-slate-600'}
                >
                  <X className="w-5 h-5" />
                </button>
              </div>

              {/* Content */}
              <div className="p-6">
                <p className={`text-sm mb-4 ${isDark ? 'text-text-secondary' : 'text-slate-600'}`}>
                  Add multiple website URLs to help Brain AI learn from your content. Enable brand extraction to automatically capture your brand identity from the first URL.
                </p>

                {/* URL Input */}
                <div className="mb-4">
                  <label className={`block text-sm font-medium mb-2 ${isDark ? 'text-text-secondary' : 'text-slate-700'}`}>
                    Add Website URL
                  </label>
                  <div className="flex gap-2">
                    <div className="relative flex-1">
                      <Globe className={`absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 ${isDark ? 'text-text-muted' : 'text-slate-400'}`} />
                      <input
                        type="url"
                        value={urlInputForList}
                        onChange={(e) => setUrlInputForList(e.target.value)}
                        placeholder="https://example.com"
                        className={`w-full pl-10 pr-4 py-2.5 rounded-xl border text-sm focus:outline-none focus:ring-2 focus:ring-violet-500 ${
                          isDark
                            ? 'bg-dark-700 border-border text-white placeholder-text-muted'
                            : 'bg-white border-slate-200 text-slate-900 placeholder-slate-400'
                        }`}
                        onKeyDown={(e) => { if (e.key === 'Enter') handleAddUrlToList(); }}
                      />
                    </div>
                    <button
                      onClick={handleAddUrlToList}
                      disabled={!urlInputForList.trim()}
                      className="btn-primary px-4 disabled:opacity-50"
                    >
                      <Plus className="w-4 h-4" />
                    </button>
                  </div>
                </div>

                {/* URL List */}
                {pendingUrls.length > 0 && (
                  <div className="mb-4">
                    <label className={`block text-sm font-medium mb-2 ${isDark ? 'text-text-secondary' : 'text-slate-700'}`}>
                      URLs to add ({pendingUrls.length})
                    </label>
                    <div className={`rounded-xl border max-h-48 overflow-y-auto ${isDark ? 'bg-dark-700 border-border' : 'bg-slate-50 border-slate-200'}`}>
                      {pendingUrls.map((url, index) => (
                        <div
                          key={url}
                          className={`flex items-center justify-between px-3 py-2 ${index !== pendingUrls.length - 1 ? (isDark ? 'border-b border-border' : 'border-b border-slate-200') : ''}`}
                        >
                          <div className="flex items-center gap-2 min-w-0 flex-1">
                            {index === 0 && analyzeAsBrand && (
                              <span className="flex-shrink-0 px-2 py-0.5 text-xs font-medium bg-violet-500/20 text-violet-400 rounded-full">
                                Brand
                              </span>
                            )}
                            <span className={`text-sm truncate ${isDark ? 'text-white' : 'text-slate-700'}`}>
                              {url}
                            </span>
                          </div>
                          <button
                            onClick={() => handleRemoveUrlFromList(url)}
                            className={`flex-shrink-0 p-1 rounded-lg transition-colors ${isDark ? 'text-text-muted hover:text-accent-red hover:bg-accent-red/10' : 'text-slate-400 hover:text-red-500 hover:bg-red-50'}`}
                          >
                            <X className="w-4 h-4" />
                          </button>
                        </div>
                      ))}
                    </div>
                  </div>
                )}

                {/* Brand Analysis Toggle */}
                <div className={`p-4 rounded-xl mb-6 ${isDark ? 'bg-dark-700' : 'bg-violet-50'}`}>
                  <div className="flex items-center justify-between mb-3">
                    <div className="flex items-center gap-3">
                      <Sparkles className={`w-5 h-5 ${analyzeAsBrand ? 'text-violet-500' : isDark ? 'text-text-muted' : 'text-slate-400'}`} />
                      <div>
                        <p className={`text-sm font-medium ${isDark ? 'text-white' : 'text-violet-900'}`}>
                          Extract brand identity
                        </p>
                        <p className={`text-xs ${isDark ? 'text-text-muted' : 'text-violet-600'}`}>
                          Analyze first URL for brand info
                        </p>
                      </div>
                    </div>
                    <button
                      onClick={() => setAnalyzeAsBrand(!analyzeAsBrand)}
                      className={`relative w-12 h-6 rounded-full transition-colors ${analyzeAsBrand ? 'bg-violet-500' : isDark ? 'bg-dark-600' : 'bg-slate-300'}`}
                    >
                      <span
                        className={`absolute top-1 w-4 h-4 rounded-full bg-white transition-all ${analyzeAsBrand ? 'left-7' : 'left-1'}`}
                      />
                    </button>
                  </div>
                  {analyzeAsBrand && (
                    <ul className={`text-xs space-y-1 ${isDark ? 'text-text-secondary' : 'text-violet-700'}`}>
                      <li className="flex items-center gap-2">
                        <Check className="w-3 h-3 text-accent-green" />
                        Brand description, voice & personality
                      </li>
                      <li className="flex items-center gap-2">
                        <Check className="w-3 h-3 text-accent-green" />
                        Logo & brand colors
                      </li>
                      <li className="flex items-center gap-2">
                        <Check className="w-3 h-3 text-accent-green" />
                        Target audience & industry
                      </li>
                    </ul>
                  )}
                </div>

                {/* Actions */}
                <div className="flex justify-end gap-3">
                  <button
                    onClick={() => {
                      setShowAnalyzeWebsiteModal(false);
                      setPendingUrls([]);
                      setUrlInputForList('');
                      setAnalyzeAsBrand(false);
                    }}
                    className={`px-4 py-2 rounded-xl text-sm font-medium border ${
                      isDark ? 'border-border text-white hover:bg-dark-700' : 'border-slate-200 text-slate-700 hover:bg-slate-50'
                    }`}
                  >
                    Cancel
                  </button>
                  <button
                    onClick={handleAddAllUrls}
                    disabled={pendingUrls.length === 0 || addingUrls}
                    className={`px-6 py-2 rounded-xl text-sm font-medium flex items-center gap-2 ${
                      pendingUrls.length === 0 || addingUrls
                        ? 'bg-violet-400 text-white cursor-not-allowed'
                        : 'bg-gradient-to-r from-violet-500 to-purple-600 text-white hover:from-violet-600 hover:to-purple-700'
                    }`}
                  >
                    {addingUrls ? (
                      <>
                        <RefreshCw className="w-4 h-4 animate-spin" />
                        Adding...
                      </>
                    ) : (
                      <>
                        <Plus className="w-4 h-4" />
                        Add {pendingUrls.length} webpage{pendingUrls.length !== 1 ? 's' : ''}
                      </>
                    )}
                  </button>
                </div>
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>

      {/* Integrations Modal */}
      <AnimatePresence>
        {showIntegrationsModal && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 z-50 flex items-center justify-center p-4"
          >
            {/* Backdrop */}
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              className="absolute inset-0 bg-black/60 backdrop-blur-sm"
              onClick={() => setShowIntegrationsModal(false)}
            />

            {/* Modal Content */}
            <motion.div
              initial={{ opacity: 0, scale: 0.95, y: 20 }}
              animate={{ opacity: 1, scale: 1, y: 0 }}
              exit={{ opacity: 0, scale: 0.95, y: 20 }}
              className={`relative w-full max-w-4xl max-h-[85vh] rounded-2xl shadow-2xl overflow-hidden ${isDark ? 'bg-dark-800' : 'bg-white'}`}
            >
              {/* Header */}
              <div className={`flex items-center justify-between p-5 border-b ${isDark ? 'border-border' : 'border-slate-200'}`}>
                <h2 className={`text-xl font-semibold ${isDark ? 'text-white' : 'text-slate-900'}`}>
                  Integrations
                </h2>
                <button
                  onClick={() => setShowIntegrationsModal(false)}
                  className={`p-2 rounded-lg transition-colors ${isDark ? 'hover:bg-dark-700 text-text-muted' : 'hover:bg-slate-100 text-slate-500'}`}
                >
                  <X className="w-5 h-5" />
                </button>
              </div>

              {/* Scrollable Content */}
              <div className="overflow-y-auto p-5" style={{ maxHeight: 'calc(85vh - 80px)' }}>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  {/* Sort providers: Connected first, then Available, then Coming Soon, alphabetically within each group */}
                  {[...providers]
                    .sort((a, b) => {
                      const aConnected = integrations.some(i => i.provider === a.id && i.status === 'connected');
                      const bConnected = integrations.some(i => i.provider === b.id && i.status === 'connected');

                      // Connected providers first
                      if (aConnected && !bConnected) return -1;
                      if (!aConnected && bConnected) return 1;

                      // Then available providers
                      if (a.isAvailable && !b.isAvailable) return -1;
                      if (!a.isAvailable && b.isAvailable) return 1;

                      // Alphabetically within same category
                      return a.name.localeCompare(b.name);
                    })
                    .map((provider) => {
                      const isConnected = integrations.some(i => i.provider === provider.id && i.status === 'connected');
                      return (
                        <div
                          key={provider.id}
                          className={`flex items-center gap-4 p-4 rounded-xl border transition-all ${
                            isDark
                              ? 'bg-dark-700 border-border hover:border-border-light'
                              : 'bg-white border-slate-200 hover:border-slate-300 shadow-sm'
                          }`}
                        >
                          <div className={`w-12 h-12 rounded-xl flex items-center justify-center ${isDark ? 'bg-dark-600' : 'bg-slate-100'}`}>
                            <ProviderIcon providerId={provider.id} providerName={provider.name} color={provider.color} connected={isConnected} />
                          </div>
                          <div className="flex-1 min-w-0">
                            <p className={`font-medium ${isDark ? 'text-white' : 'text-slate-900'}`}>
                              {provider.name}
                            </p>
                            <p className={`text-sm line-clamp-2 ${isDark ? 'text-text-muted' : 'text-slate-500'}`}>
                              {provider.description}
                            </p>
                          </div>
                          {isConnected ? (
                            <div className="flex items-center gap-2">
                              <div className="flex flex-col items-end">
                                <span className="badge bg-accent-green/20 text-accent-green text-xs px-3 py-1.5 whitespace-nowrap">
                                  Connected
                                </span>
                                {(() => {
                                  const integration = integrations.find(i => i.provider === provider.id);
                                  if (integration?.provider_username && (provider.id === 'facebook' || provider.id === 'instagram')) {
                                    return (
                                      <span className={`text-xs mt-1 max-w-[150px] truncate ${isDark ? 'text-text-muted' : 'text-slate-500'}`} title={integration.provider_email || integration.provider_username}>
                                        {integration.provider_username}
                                      </span>
                                    );
                                  }
                                  return null;
                                })()}
                              </div>
                              <button
                                onClick={() => {
                                  const integration = integrations.find(i => i.provider === provider.id);
                                  if (integration) {
                                    disconnectIntegration(integration.id);
                                  }
                                }}
                                className={`p-2 rounded-lg transition-colors ${
                                  isDark
                                    ? 'hover:bg-red-500/20 text-text-muted hover:text-red-400'
                                    : 'hover:bg-red-50 text-slate-400 hover:text-red-500'
                                }`}
                                title="Disconnect"
                              >
                                <Trash2 className="w-4 h-4" />
                              </button>
                            </div>
                          ) : (
                            <button
                              onClick={() => {
                                connectProvider(provider);
                                setShowIntegrationsModal(false);
                              }}
                              disabled={connecting || !provider.isAvailable}
                              className={`px-4 py-2 text-sm font-medium rounded-lg transition-colors whitespace-nowrap ${
                                provider.isAvailable
                                  ? 'btn-primary'
                                  : isDark
                                    ? 'bg-dark-600 text-text-muted cursor-not-allowed'
                                    : 'bg-slate-100 text-slate-400 cursor-not-allowed'
                              }`}
                            >
                              {provider.isAvailable ? 'Connect' : 'Coming Soon'}
                            </button>
                          )}
                        </div>
                      );
                    })}

                  {/* If no providers available */}
                  {providers.length === 0 && (
                    <div className={`col-span-2 text-center py-12 ${isDark ? 'text-text-muted' : 'text-slate-500'}`}>
                      <Zap className="w-12 h-12 mx-auto mb-4 opacity-50" />
                      <p className="text-lg font-medium mb-2">No integrations available</p>
                      <p className="text-sm">Check back soon for new integrations!</p>
                    </div>
                  )}
                </div>
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>
    </motion.div>
  );
}
